import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { client } from "../api";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormControl,
  Grid,
  LinearProgress,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Radio,
  RadioGroup,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import GradeRoundedIcon from "@mui/icons-material/GradeRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";

const DASHBOARD_SELECTED_TENANT_KEY = "admin_dashboard_selected_tenant_id";

/**
 * Coerce an API error value into a displayable string. FastAPI/Pydantic 422s return
 * `detail` as an array of objects; rendering that directly as a React child crashes the
 * page ("Objects are not valid as a React child"), so always normalize before display.
 */
function formatApiError(value, fallback = "Something went wrong") {
  if (typeof value === "string") return value.trim() || fallback;
  if (Array.isArray(value)) {
    const msg = value
      .map((e) => (typeof e === "string" ? e : e?.msg))
      .filter(Boolean)
      .join("; ");
    return msg || fallback;
  }
  if (value && typeof value === "object") {
    return value.msg || value.detail || fallback;
  }
  return fallback;
}

/**
 * Storefront-search install snippet. The retrieval logic is platform-agnostic; only two CSS
 * selectors change per storefront theme — the search input, and the theme's native autocomplete
 * dropdown to suppress (leave empty if the theme has none).
 */
const STOREFRONT_SEARCH_PLATFORMS = {
  magento: {
    label: "Magento (Hyvä theme)",
    input: "#search",
    native: ".search-block .absolute.top-8, .adobe-search .absolute.top-8",
  },
  woocommerce: {
    label: "WordPress / WooCommerce",
    input: "input.search-field, #s",
    native: "",
  },
  other: {
    label: "Other / custom theme",
    input: "REPLACE_WITH_YOUR_SEARCH_INPUT_SELECTOR",
    native: "",
  },
};

function buildStorefrontSearchSnippet(searchInput, nativeDropdown) {
  return `<script>
(function () {
  // ===== Set these two selectors for your storefront theme =====
  var SEARCH_INPUT = ${JSON.stringify(searchInput)};            // the search <input>
  var NATIVE_DROPDOWN = ${JSON.stringify(nativeDropdown)};      // theme's native suggestion box ('' if none)
  // =============================================================
  var CFG = window.CHATBOT_WIDGET_CONFIG; if (!CFG) return;
  var api = String(CFG.apiUrl || '').replace(/\\/$/, '');
  var KEY = CFG.tenantPublicKey;
  function debounce(fn, ms){ var t; return function(){ var a=arguments,c=this; clearTimeout(t); t=setTimeout(function(){fn.apply(c,a);},ms); }; }
  function money(p, c){ if(p==null||!(Number(p)>0)) return ''; var s={GBP:'£',USD:'$',EUR:'€'}[c]||(c?c+' ':''); return s+Number(p).toFixed(2); }
  function beacon(p, pos){ try{ var body=JSON.stringify({ query:((document.querySelector(SEARCH_INPUT)||{}).value||'').trim(), product_id:String(p.id||p.product_id||p.url||''), product_name:p.title||'', product_url:p.url||'', position:pos, event_type:'click' }); fetch(api+'/api/public/search/click',{method:'POST',keepalive:true,headers:{'Content-Type':'application/json','X-Widget-Key':KEY},body:body}).catch(function(){}); }catch(e){} }
  function hideNativeNow(){ if(!NATIVE_DROPDOWN) return; document.querySelectorAll(NATIVE_DROPDOWN).forEach(function(el){ el.style.setProperty('display','none','important'); }); }
  function ensureStyle(){ if(!NATIVE_DROPDOWN || document.getElementById('cb-hide-native')) return; var st=document.createElement('style'); st.id='cb-hide-native'; st.textContent=NATIVE_DROPDOWN+'{display:none!important;}'; (document.head||document.documentElement).appendChild(st); }
  function ensureBox(input){ var box=document.getElementById('cb-suggest-box'); if(!box){ box=document.createElement('div'); box.id='cb-suggest-box'; box.style.cssText='position:absolute;z-index:100000;background:#fff;border:1px solid #ddd;border-radius:8px;box-shadow:0 8px 30px rgba(0,0,0,.12);max-height:60vh;overflow:auto;display:none;'; document.body.appendChild(box); box.addEventListener('mousedown', function(e){ var t=e.target; var aq=t&&t.closest?t.closest('a[data-cb-q]'):null; if(aq){ e.preventDefault(); var inp=document.querySelector(SEARCH_INPUT); if(inp){ inp.value=aq.getAttribute('data-cb-q'); try{inp.dispatchEvent(new Event('input',{bubbles:true}));}catch(e2){} inp.focus(); } return; } var a=t&&t.closest?t.closest('a[data-cb-idx]'):null; if(!a) return; var i=parseInt(a.getAttribute('data-cb-idx'),10); var p=(box.__cbP||[])[i]; if(p) beacon(p, i+1); }); } var r=input.getBoundingClientRect(); box.style.left=(r.left+window.scrollX)+'px'; box.style.top=(r.bottom+window.scrollY+4)+'px'; box.style.width=r.width+'px'; return box; }
  function pcard(p, i){ var pl=money(p.price,p.currency); var ph=pl?'<br><span style="color:#666;font-size:13px;">'+pl+'</span>':'<br><span style="color:#888;font-size:13px;">Price on request</span>'; return '<a href="'+(p.url||'#')+'" data-cb-idx="'+i+'" style="display:flex;gap:10px;align-items:center;padding:10px 12px;text-decoration:none;color:#222;border-bottom:1px solid #f0f0f0;">'+(p.image_url?'<img src="'+p.image_url+'" style="width:40px;height:40px;object-fit:contain;border:1px solid #eee;border-radius:6px;">':'')+'<span style="flex:1;"><span style="font-weight:600;">'+(p.title||'')+'</span>'+ph+'</span></a>'; }
  function clink(c){ var u=(c.url||'').trim(); if(u && u!=='#'){ return '<a href="'+u+'" target="_blank" rel="noopener" style="display:block;padding:8px 12px;text-decoration:none;color:#333;border-bottom:1px solid #f5f5f5;font-size:14px;">'+(c.name||'')+'</a>'; } return '<div style="padding:8px 12px;color:#333;border-bottom:1px solid #f5f5f5;font-size:14px;">'+(c.name||'')+'</div>'; }
  function qrow(q){ return '<a href="#" data-cb-q="'+String(q).replace(/"/g,'&quot;')+'" style="display:block;padding:8px 12px;text-decoration:none;color:#333;border-bottom:1px solid #f5f5f5;font-size:14px;">'+q+'</a>'; }
  function hdr(t){ return '<div style="padding:6px 12px;font-size:11px;text-transform:uppercase;color:#999;font-weight:700;background:#fafafa;">'+t+'</div>'; }
  function render(box, d){ d=d||{}; var html=''; var prods=[];
    if(d.no_results && d.message){ html+='<div style="padding:10px 12px;color:#666;font-size:14px;border-bottom:1px solid #f0f0f0;">'+d.message+'</div>'; }
    var products=d.products||[]; if(products.length){ html+=products.map(function(p){ prods.push(p); return pcard(p, prods.length-1); }).join(''); }
    if((d.categories||[]).length){ html+=hdr('Categories')+d.categories.map(clink).join(''); }
    if((d.popular||[]).length){ html+=hdr('Popular searches')+d.popular.map(qrow).join(''); }
    if((d.recent||[]).length){ html+=hdr('Recent searches')+d.recent.map(qrow).join(''); }
    var zr=d.zero_result;
    if(zr){
      if(zr.redirect){ html+=hdr('Suggested page')+'<a href="'+zr.redirect+'" style="display:block;padding:10px 12px;color:#b23;font-weight:600;text-decoration:none;">Go to suggested page &rarr;</a>'; }
      if((zr.alternatives||[]).length){ html+=hdr('Try instead')+zr.alternatives.map(qrow).join(''); }
      if((zr.related||[]).length){ html+=hdr('You might like')+zr.related.map(function(p){ prods.push(p); return pcard(p, prods.length-1); }).join(''); }
      if((zr.trending||[]).length){ html+=hdr('Trending')+zr.trending.map(function(p){ prods.push(p); return pcard(p, prods.length-1); }).join(''); }
      if((zr.popular_categories||[]).length){ html+=hdr('Popular categories')+zr.popular_categories.map(clink).join(''); }
    }
    if(!html){ box.style.display='none'; return; }
    box.__cbP=prods; box.innerHTML=html; box.style.display='block'; }
  function activate(){
    var input=document.querySelector(SEARCH_INPUT); if(!input) return;
    window.__cbSearchOn=true; ensureStyle(); hideNativeNow();
    try { var root=input.closest('form')||document.body; new MutationObserver(hideNativeNow).observe(root,{childList:true,subtree:true,attributes:true,attributeFilter:['style','class']}); } catch(e){}
    var ctrl;
    var run=debounce(function(){ var q=(input.value||'').trim(); var box=ensureBox(input); if(q.length<2){ box.style.display='none'; return; } if(ctrl){ try{ctrl.abort();}catch(e){} } ctrl=('AbortController' in window)?new AbortController():null; fetch(api+'/api/public/suggest?limit=6',{method:'POST',signal:ctrl?ctrl.signal:undefined,headers:{'Content-Type':'application/json','X-Widget-Key':KEY},body:JSON.stringify({query:q})}).then(function(r){return r.ok?r.json():{products:[]};}).then(function(d){ render(box,d||{}); }).catch(function(){}); },600);
    input.addEventListener('input', function(e){ e.stopImmediatePropagation(); hideNativeNow(); run(); }, true);
    input.addEventListener('focus', function(){ hideNativeNow(); if((input.value||'').trim().length>=2) run(); }, true);
    document.addEventListener('click', function(e){ var box=document.getElementById('cb-suggest-box'); if(box && e.target!==input && !box.contains(e.target)) box.style.display='none'; });
    var form=input.closest('form'); if(form){ form.addEventListener('submit', function(e){ var q=(input.value||'').trim(); if(!q) return; e.preventDefault(); e.stopImmediatePropagation(); var box=document.getElementById('cb-suggest-box'); if(box) box.style.display='none'; if(window.ChatbotWidget && window.ChatbotWidget.ask) window.ChatbotWidget.ask(q); }, true); }
  }
  window.addEventListener('pageshow', function(){ if(window.__cbSearchOn){ ensureStyle(); hideNativeNow(); var b=document.getElementById('cb-suggest-box'); if(b) b.style.display='none'; } });
  fetch(api+'/api/public/config',{cache:'no-store',headers:{'X-Widget-Key':KEY}}).then(function(r){return r.ok?r.json():null;}).then(function(cfg){ if(cfg && cfg.storefront_search_enabled){ document.readyState==='loading'?document.addEventListener('DOMContentLoaded',activate):activate(); }}).catch(function(){});
})();
</${"script"}>`;
}

/** Valid source_mode values per source_db_type (must match backend resolve_source_plan). */
const SOURCE_MODE_BY_DB_TYPE = {
  wordpress: [
    { value: "wordpress", label: "WordPress only" },
    { value: "static", label: "Static URLs only" },
    { value: "mixed", label: "WordPress + Static URLs" },
  ],
  woocommerce: [
    { value: "wordpress", label: "Catalog + WordPress content" },
    { value: "static", label: "Static URLs only" },
    { value: "mixed", label: "Catalog + Static URLs" },
  ],
  magento: [
    { value: "magento", label: "Magento catalog only" },
    { value: "mixed", label: "Magento + Static URLs" },
    { value: "static", label: "Static URLs only" },
  ],
  static: [{ value: "static", label: "Static URLs only" }],
};

const normalizeSourceModeForDbType = (dbType, mode) => {
  const options = SOURCE_MODE_BY_DB_TYPE[dbType] || SOURCE_MODE_BY_DB_TYPE.wordpress;
  const allowed = options.map((o) => o.value);
  const raw = (mode || "").trim().toLowerCase();
  if (allowed.includes(raw)) return raw;
  return allowed[0];
};

const defaultTablePrefixForDbType = (dbType) => {
  if (dbType === "magento" || dbType === "static") return "";
  return "wp_";
};

const defaultUrlTableForDbType = (dbType) => {
  if (dbType === "magento") return "1";
  if (dbType === "static") return "";
  return "wp_custom_urls";
};

const applySourceDefaultsForDbType = (dbType, { tablePrefix, urlTable }) => {
  const nextPrefix = tablePrefix ?? defaultTablePrefixForDbType(dbType);
  const nextUrlTable = urlTable ?? defaultUrlTableForDbType(dbType);
  return {
    tablePrefix: dbType === "magento" && (nextPrefix === "wp_" || nextPrefix === "wp") ? "" : nextPrefix,
    urlTable: dbType === "magento" && nextUrlTable === "wp_custom_urls" ? "1" : nextUrlTable,
  };
};

const adminApiBase = () => String(client.defaults.baseURL || "").replace(/\/+$/, "");

/** Normalized reindex progress from API (planned total vs processed). */
const reindexJobProgress = (job) => {
  const p = job?.meta?.progress;
  if (!p) return null;
  const total = Number(p.total_items ?? p.planned_total_records ?? 0);
  const processed = Number(p.processed_items ?? 0);
  const pct = Number(
    p.progress_percentage ?? (total > 0 ? Math.min(100, (processed / total) * 100) : 0),
  );
  return {
    total,
    processed,
    remaining: Number(p.remaining_items ?? Math.max(0, total - processed)),
    failed: Number(p.failed_items ?? 0),
    percentage: pct,
  };
};

/** Same rule as widget: API base + `/api/assets/...` (or legacy URL containing that path). */
const adminBrandingAvatarSrc = (raw) => {
  if (!raw) return "";
  const base = adminApiBase();
  if (!base) return raw;
  let path = String(raw).trim();
  if (!path.startsWith("/")) {
    const marker = "/api/assets/";
    const i = path.indexOf(marker);
    path = i >= 0 ? path.slice(i) : path;
  }
  if (!path.startsWith("/")) return path;
  return `${base}${path}`;
};

const AdminDashboard = ({ role, userEmail, onLogout, tenantId, tenantIds = [] }) => {
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [selectedVisitorId, setSelectedVisitorId] = useState("");
  const [selectedVisitorSessionId, setSelectedVisitorSessionId] = useState("");
  const [visitorChats, setVisitorChats] = useState([]);
  const [chatsPage, setChatsPage] = useState(1);
  const [chatsTotal, setChatsTotal] = useState(0);
  const [adminsPage, setAdminsPage] = useState(1);
  const [adminsTotal, setAdminsTotal] = useState(0);
  const [visitorsPage, setVisitorsPage] = useState(1);
  const [visitorsTotal, setVisitorsTotal] = useState(0);
  const [visitorChatsPage, setVisitorChatsPage] = useState(1);
  const [visitorChatsTotal, setVisitorChatsTotal] = useState(0);
  const [tenants, setTenants] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminTenantId, setAdminTenantId] = useState("");
  const [adminTenantMode, setAdminTenantMode] = useState("existing");
  const [newUserRole, setNewUserRole] = useState(role === "admin" ? "manager" : "admin");
  const [newTenantName, setNewTenantName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [chatFilter, setChatFilter] = useState("");
  const [sourceTenantId, setSourceTenantId] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(DASHBOARD_SELECTED_TENANT_KEY) || "" : "",
  );
  const [sourceDbUrl, setSourceDbUrl] = useState("");
  const [sourceDbType, setSourceDbType] = useState("wordpress");
  const [sourceTablePrefix, setSourceTablePrefix] = useState("");
  const [sourceUrlTable, setSourceUrlTable] = useState("wp_custom_urls");
  const [sourceMode, setSourceMode] = useState("wordpress");
  const [sourceStaticUrlsJson, setSourceStaticUrlsJson] = useState("");
  const [sourceDomainAliases, setSourceDomainAliases] = useState("");
  const [sourceCanonicalBaseUrl, setSourceCanonicalBaseUrl] = useState("");
  const [brandName, setBrandName] = useState("");
  const [widgetPrimaryColor, setWidgetPrimaryColor] = useState("#bf362e");
  const [widgetWebsiteUrl, setWidgetWebsiteUrl] = useState("");
  const [widgetSourceType, setWidgetSourceType] = useState("");
  const [widgetUserMessageColor, setWidgetUserMessageColor] = useState("#bf362e");
  const [widgetBotMessageColor, setWidgetBotMessageColor] = useState("#d5bbb9");
  const [widgetUserMessageTextColor, setWidgetUserMessageTextColor] = useState("#ffffff");
  const [widgetBotMessageTextColor, setWidgetBotMessageTextColor] = useState("#1a1a1a");
  const [widgetHeaderTitle, setWidgetHeaderTitle] = useState("");
  const [widgetWelcomeMessage, setWidgetWelcomeMessage] = useState("");
  const [chatMaxResults, setChatMaxResults] = useState("");
  const [chatMaxResultsCatalog, setChatMaxResultsCatalog] = useState("");
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");
  const [corsAllowedOrigins, setCorsAllowedOrigins] = useState("");
  const [avatarUpload, setAvatarUpload] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState("");
  const [clearBrandingAvatar, setClearBrandingAvatar] = useState(false);
  const [monthlyMessageLimit, setMonthlyMessageLimit] = useState(15000);
  const [quotaReachedMessage, setQuotaReachedMessage] = useState("Monthly message limit reached. Please try again next month.");
  const [idleRatingWaitSeconds, setIdleRatingWaitSeconds] = useState(120);
  const [transcriptEmailEnabled, setTranscriptEmailEnabled] = useState(false);
  const [transcriptEmailTo, setTranscriptEmailTo] = useState("");
  const [recaptchaStatus, setRecaptchaStatus] = useState(null);
  const [recaptchaTesting, setRecaptchaTesting] = useState(false);
  const [recaptchaSiteKeyInput, setRecaptchaSiteKeyInput] = useState("");
  const [recaptchaSecretKeyInput, setRecaptchaSecretKeyInput] = useState("");
  const [recaptchaMinScore, setRecaptchaMinScore] = useState(0.5);
  const [recaptchaSecretSet, setRecaptchaSecretSet] = useState(false);
  const [recaptchaSaving, setRecaptchaSaving] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [storefrontSearchEnabled, setStorefrontSearchEnabled] = useState(false);
  const [poweredByText, setPoweredByText] = useState("Powered by Company Name");
  const [storefrontPlatform, setStorefrontPlatform] = useState("magento");
  const [searchSnippetCopied, setSearchSnippetCopied] = useState(false);
  const [blockedIps, setBlockedIps] = useState([]);
  const [blockedCountries, setBlockedCountries] = useState([]);
  const [newBlockedIp, setNewBlockedIp] = useState("");
  const [newBlockedIpReason, setNewBlockedIpReason] = useState("");
  const [newBlockedCountry, setNewBlockedCountry] = useState("");
  const [newBlockedCountryReason, setNewBlockedCountryReason] = useState("");
  const [countryReference, setCountryReference] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [manageTenantsOpen, setManageTenantsOpen] = useState(false);
  const [manageTenantUser, setManageTenantUser] = useState(null);
  const [manageTenantIds, setManageTenantIds] = useState([]);
  const [manageTenantSaving, setManageTenantSaving] = useState(false);
  const [createTenantOpen, setCreateTenantOpen] = useState(false);
  const [newStandaloneTenantName, setNewStandaloneTenantName] = useState("");
  const [blockWordCategories, setBlockWordCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryMode, setNewCategoryMode] = useState("exact");
  const [newCategoryResponse, setNewCategoryResponse] = useState("");
  const [newWordByCategory, setNewWordByCategory] = useState({});
  const [moderationPanel, setModerationPanel] = useState(0);
  const [quickReplies, setQuickReplies] = useState([]);
  const [newQrCategory, setNewQrCategory] = useState("general");
  const [newQrTrigger, setNewQrTrigger] = useState("");
  const [newQrTemplate, setNewQrTemplate] = useState("");
  const [newQrPriority, setNewQrPriority] = useState("0");
  const [newQrThreshold, setNewQrThreshold] = useState("");
  const [quickReplyDrafts, setQuickReplyDrafts] = useState({});
  const [retrievalProfile, setRetrievalProfile] = useState(null);
  const [retrievalProfileLoading, setRetrievalProfileLoading] = useState(false);
  const [expandedFacetAliases, setExpandedFacetAliases] = useState({});
  const [profileJsonDraft, setProfileJsonDraft] = useState("");
  const [profileJsonOpen, setProfileJsonOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [gazetteerDrafts, setGazetteerDrafts] = useState({});
  const [success, setSuccess] = useState("");
  // --- AI Search admin module ---
  const [searchAnalytics, setSearchAnalytics] = useState(null);
  const [searchDays, setSearchDays] = useState(30);
  const [searchChannel, setSearchChannel] = useState("");
  const [searchLogs, setSearchLogs] = useState([]);
  const [searchLogsTotal, setSearchLogsTotal] = useState(0);
  const [searchLogsPage, setSearchLogsPage] = useState(1);
  const [searchLogQuery, setSearchLogQuery] = useState("");
  const [searchLogZeroOnly, setSearchLogZeroOnly] = useState(false);
  const [searchConfig, setSearchConfig] = useState(null);
  const [searchConfigSaving, setSearchConfigSaving] = useState(false);
  const [searchIndexStatus, setSearchIndexStatus] = useState(null);
  const searchLogQueryRef = useRef("");
  const sessionRequestRef = useRef(0);
  const countriesLoadedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const canSelectTenant = role === "superadmin" || tenantIds.length > 1 || tenants.length > 1;
  const effectiveTenantId = sourceTenantId || tenantId || undefined;
  const PAGE_SIZE = 10;
  const routeTail = (location.pathname.replace("/admin/dashboard/", "") || "overview").replace(/^\/+/, "");
  const routeParts = routeTail.split("/").filter(Boolean);
  const section = routeParts[0] || "overview";
  const usersSubsection = section === "users" ? (routeParts[1] || "admins") : "admins";
  const settingsSubsection = section === "settings" ? (routeParts[1] || "general") : "general";
  const searchSubsection = section === "search" ? (routeParts[1] || "analytics") : "analytics";

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === sourceTenantId),
    [tenants, sourceTenantId],
  );
  const [embedCopied, setEmbedCopied] = useState(false);
  const [keyBusy, setKeyBusy] = useState(false);
  const embedConfig = useMemo(() => {
    const origin =
      typeof window !== "undefined"
        ? `${window.location.protocol}//${window.location.host}`
        : "";
    const runtime = (typeof window !== "undefined" && window.RUNTIME_CONFIG) || {};
    const baseUrl = runtime.WIDGET_BASE_URL || origin;
    const apiUrl = runtime.API_BASE_URL || process.env.REACT_APP_API_URL || origin;
    const tenantPublicKey = selectedTenant?.widget_public_key || "";
    const snippet = `<script>
  window.CHATBOT_WIDGET_CONFIG = {
    baseUrl: '${baseUrl}',
    apiUrl: '${apiUrl}',
    tenantPublicKey: '${tenantPublicKey || "replace-with-widget-key"}'
  };
  (function () {
    const script = document.createElement('script');
    script.src = window.CHATBOT_WIDGET_CONFIG.baseUrl + '/widget.js';
    script.async = true;
    document.body.appendChild(script);
  })();
</script>`;
    return { baseUrl, apiUrl, tenantPublicKey, snippet };
  }, [selectedTenant]);
  const copyEmbedSnippet = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(embedConfig.snippet);
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
    } catch {
      /* clipboard unavailable; user can still select the text manually */
    }
  }, [embedConfig.snippet]);
  const searchSnippet = useMemo(() => {
    const p = STOREFRONT_SEARCH_PLATFORMS[storefrontPlatform] || STOREFRONT_SEARCH_PLATFORMS.magento;
    return buildStorefrontSearchSnippet(p.input, p.native);
  }, [storefrontPlatform]);
  const copySearchSnippet = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(searchSnippet);
      setSearchSnippetCopied(true);
      setTimeout(() => setSearchSnippetCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  }, [searchSnippet]);
  const brandingAvatarPreviewSrc = useMemo(() => {
    if (avatarPreviewUrl) return avatarPreviewUrl;
    if (!selectedTenant?.avatar_url || clearBrandingAvatar) return "";
    return adminBrandingAvatarSrc(selectedTenant.avatar_url);
  }, [selectedTenant, clearBrandingAvatar, avatarPreviewUrl]);

  const countryLabelByCode = useMemo(() => {
    const m = {};
    (countryReference || []).forEach((c) => {
      if (c?.code) m[c.code] = c.name;
    });
    return m;
  }, [countryReference]);

  const navigateSection = (nextSection) => {
    navigate(`/admin/dashboard/${nextSection}`);
  };

  const navigateUsersSubsection = (nextSubsection) => {
    navigate(`/admin/dashboard/users/${nextSubsection}`);
  };

  const navigateSettingsSubsection = (nextSubsection) => {
    navigate(`/admin/dashboard/settings/${nextSubsection}`);
  };

  const navigateSearchSubsection = (nextSubsection) => {
    navigate(`/admin/dashboard/search/${nextSubsection}`);
  };

  const persistSourceTenantId = useCallback((value) => {
    setSourceTenantId(value);
    if (value) {
      localStorage.setItem(DASHBOARD_SELECTED_TENANT_KEY, value);
    } else {
      localStorage.removeItem(DASHBOARD_SELECTED_TENANT_KEY);
    }
  }, []);

  const refreshJobsOnly = useCallback(async () => {
    try {
      const { data } = await client.get("/api/reindex/jobs", { params: { tenant_id: effectiveTenantId } });
      setJobs(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading jobs");
    }
  }, [effectiveTenantId]);

  const loadSecuritySettings = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/security`);
      setBlockedIps(data.blocked_ips || []);
      setBlockedCountries(data.blocked_countries || []);
      setMonthlyMessageLimit(Number(data.monthly_message_limit || 15000));
      setQuotaReachedMessage(data.quota_reached_message || "Monthly message limit reached. Please try again next month.");
      setIdleRatingWaitSeconds(Number(data.idle_rating_wait_seconds || 120));
      setCorsAllowedOrigins(data.cors_allowed_origins || "");
      setTranscriptEmailEnabled(Boolean(data.transcript_email_enabled));
      setTranscriptEmailTo(data.transcript_email_to || "");
      setAiEnabled(data.ai_enabled !== false);
      setStorefrontSearchEnabled(Boolean(data.storefront_search_enabled));
      setPoweredByText(data.widget_powered_by_text ?? "Powered by Company Name");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading security settings");
    }
  }, [effectiveTenantId]);

  const loadRecaptchaSettings = useCallback(async () => {
    if (role !== "superadmin") return;
    try {
      const { data } = await client.get(`/api/admin/recaptcha-settings`);
      setRecaptchaSiteKeyInput(data?.site_key || "");
      setRecaptchaMinScore(Number(data?.min_score ?? 0.5));
      setRecaptchaSecretSet(Boolean(data?.secret_key_set));
    } catch (err) {
      /* non-superadmin or not available */
    }
  }, [role]);

  const loadBlockWordSettings = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/block-word-categories`);
      setBlockWordCategories(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading block-word settings");
    }
  }, [effectiveTenantId]);

  const loadQuickReplies = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/quick-replies`);
      setQuickReplies(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading quick replies");
    }
  }, [effectiveTenantId]);

  const loadRetrievalProfile = useCallback(async () => {
    if (!effectiveTenantId) return;
    setRetrievalProfileLoading(true);
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/retrieval-profile`);
      setRetrievalProfile(data);
      setError("");
    } catch (err) {
      setRetrievalProfile(null);
      setError(err?.response?.data?.detail || "Failed loading retrieval profile");
    } finally {
      setRetrievalProfileLoading(false);
    }
  }, [effectiveTenantId]);

  const openProfileJsonEditor = useCallback(() => {
    const profile = retrievalProfile?.profile;
    setProfileJsonDraft(profile ? JSON.stringify(profile, null, 2) : "{}");
    setProfileJsonOpen(true);
  }, [retrievalProfile]);

  const saveProfileJson = useCallback(async () => {
    if (!effectiveTenantId) return;
    setProfileSaving(true);
    try {
      const parsed = JSON.parse(profileJsonDraft);
      const { data } = await client.put(`/api/admin/tenants/${effectiveTenantId}/retrieval-profile`, {
        profile: parsed,
      });
      setRetrievalProfile(data);
      setProfileJsonOpen(false);
      setSuccess("Retrieval profile updated.");
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to save retrieval profile");
    } finally {
      setProfileSaving(false);
    }
  }, [effectiveTenantId, profileJsonDraft]);

  const saveGazetteerFlags = useCallback(async () => {
    if (!effectiveTenantId || !retrievalProfile?.profile) return;
    const entries = Object.entries(gazetteerDrafts).map(([id, draft]) => ({
      id,
      hard_filter: draft.hard_filter ?? null,
      demote_accessory_substrings: draft.demote_accessory_substrings ?? null,
      fixture_stem: draft.fixture_stem ?? null,
      accessory_keywords: draft.accessory_keywords ?? null,
    }));
    if (!entries.length) return;
    setProfileSaving(true);
    try {
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/retrieval-profile/gazetteer`, { entries });
      await loadRetrievalProfile();
      setGazetteerDrafts({});
      setSuccess("Gazetteer match flags updated.");
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update gazetteer flags");
    } finally {
      setProfileSaving(false);
    }
  }, [effectiveTenantId, gazetteerDrafts, loadRetrievalProfile, retrievalProfile]);

  const loadTenants = useCallback(async () => {
    try {
      const { data } = await client.get("/api/admin/tenants");
      setTenants(data || []);
      if (!sourceTenantId && data?.length > 0) {
        const firstTenant = tenantIds[0] || tenantId || data[0].id;
        const next = firstTenant || "";
        persistSourceTenantId(next);
      }
      if (!adminTenantId && data?.length > 0) {
        const firstTenant = tenantIds[0] || tenantId || data[0].id;
        setAdminTenantId(firstTenant || "");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading tenants");
    }
  }, [adminTenantId, sourceTenantId, tenantId, tenantIds, persistSourceTenantId]);

  const regenerateWidgetKey = useCallback(async () => {
    if (!effectiveTenantId) return;
    const hasKey = !!selectedTenant?.widget_public_key;
    if (hasKey && !window.confirm("Regenerate the widget key? The current key will stop working and any site using it must be updated.")) {
      return;
    }
    setKeyBusy(true);
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/widget-key`);
      await loadTenants();
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed generating widget key");
    } finally {
      setKeyBusy(false);
    }
  }, [effectiveTenantId, selectedTenant, loadTenants]);

  const loadOverviewSection = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get("/api/admin/overview", { params: { tenant_id: effectiveTenantId } });
      setOverview(data);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading overview");
    }
  }, [effectiveTenantId]);

  const loadSearchAnalytics = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get("/api/admin/search/analytics", {
        params: { tenant_id: effectiveTenantId, days: searchDays, channel: searchChannel || undefined },
      });
      setSearchAnalytics(data);
      setError("");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Failed loading search analytics"));
    }
  }, [effectiveTenantId, searchDays, searchChannel]);

  const loadSearchLogs = useCallback(async (page = 1) => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get("/api/admin/search/logs", {
        params: {
          tenant_id: effectiveTenantId,
          page,
          page_size: PAGE_SIZE,
          q: searchLogQueryRef.current || undefined,
          channel: searchChannel || undefined,
          zero_only: searchLogZeroOnly || undefined,
        },
      });
      setSearchLogs(data?.items || []);
      setSearchLogsTotal(Number(data?.total || 0));
      setSearchLogsPage(Number(data?.page || page));
      setError("");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Failed loading search logs"));
    }
    // NB: searchLogQuery is read from a ref (not deps) so typing doesn't refetch on every keystroke;
    // it only applies on Apply/Enter. Channel + zero-only changes do auto-refetch.
  }, [effectiveTenantId, searchChannel, searchLogZeroOnly]);

  const loadSearchConfig = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const [cfgRes, idxRes] = await Promise.all([
        client.get("/api/admin/search/config", { params: { tenant_id: effectiveTenantId } }),
        client.get("/api/admin/search/index-status", { params: { tenant_id: effectiveTenantId } }),
      ]);
      setSearchConfig(cfgRes.data);
      setSearchIndexStatus(idxRes.data);
      setError("");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Failed loading search settings"));
    }
  }, [effectiveTenantId]);

  const saveSearchConfig = useCallback(async (patch) => {
    if (!effectiveTenantId) return;
    setSearchConfigSaving(true);
    try {
      const { data } = await client.patch("/api/admin/search/config", patch, {
        params: { tenant_id: effectiveTenantId },
      });
      setSearchConfig(data);
      setSuccess("Search settings saved");
      setError("");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Failed saving search settings"));
    } finally {
      setSearchConfigSaving(false);
    }
  }, [effectiveTenantId]);

  const runReindexFromSearch = useCallback(async (mode = "full") => {
    if (!effectiveTenantId) return;
    try {
      await client.post("/api/reindex", { tenant_id: effectiveTenantId, mode });
      setSuccess(mode === "incremental" ? "Incremental reindex started" : "Full reindex started");
      setTimeout(() => setSuccess(""), 2500);
      const { data } = await client.get("/api/admin/search/index-status", {
        params: { tenant_id: effectiveTenantId },
      });
      setSearchIndexStatus(data);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Failed to start reindex"));
    }
  }, [effectiveTenantId]);

  const exportSearchCsv = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const res = await client.get("/api/admin/search/export", {
        params: { tenant_id: effectiveTenantId, days: searchDays, channel: searchChannel || undefined },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `search-analytics-${searchDays}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Failed to export"));
    }
  }, [effectiveTenantId, searchDays, searchChannel]);

  const setCfgField = (key, value) => setSearchConfig((c) => ({ ...(c || {}), [key]: value }));
  const setAutocompleteField = (key, value) =>
    setSearchConfig((c) => ({ ...(c || {}), autocomplete: { ...((c || {}).autocomplete || {}), [key]: value } }));

  const loadChatsSection = useCallback(async (query = chatFilter, page = 1) => {
    if (!effectiveTenantId) return;
    try {
      const [chatRes, usageRes] = await Promise.all([
        client.get("/api/admin/chats", {
          params: { q: query || undefined, tenant_id: effectiveTenantId, page, page_size: PAGE_SIZE },
        }),
        client.get("/api/admin/usage/summary", { params: { tenant_id: effectiveTenantId } }),
      ]);
      setSessions(chatRes.data?.items || []);
      setChatsTotal(Number(chatRes.data?.total || 0));
      setChatsPage(Number(chatRes.data?.page || page));
      setUsageSummary(usageRes.data);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading chats");
    }
  }, [effectiveTenantId, chatFilter]);

  const loadJobsSection = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get("/api/reindex/jobs", { params: { tenant_id: effectiveTenantId } });
      setJobs(data || []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading jobs");
    }
  }, [effectiveTenantId]);

  const loadAdminsSection = useCallback(async (page = 1) => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get("/api/admin/users", {
        params: { tenant_id: effectiveTenantId, page, page_size: PAGE_SIZE },
      });
      setUsers(data?.items || []);
      setAdminsTotal(Number(data?.total || 0));
      setAdminsPage(Number(data?.page || page));
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading admins");
    }
  }, [effectiveTenantId]);

  const loadVisitorsSection = useCallback(async (page = 1) => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get("/api/admin/visitors", {
        params: { tenant_id: effectiveTenantId, page, page_size: PAGE_SIZE },
      });
      setVisitors(data?.items || []);
      setVisitorsTotal(Number(data?.total || 0));
      setVisitorsPage(Number(data?.page || page));
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading visitors");
    }
  }, [effectiveTenantId]);

  useEffect(() => {
    if (section !== "jobs") return undefined;
    const id = setInterval(() => {
      refreshJobsOnly();
    }, 5000);
    return () => clearInterval(id);
  }, [section, refreshJobsOnly]);

  useEffect(() => {
    if (!tenants.length || !sourceTenantId) return;
    if (tenants.some((t) => t.id === sourceTenantId)) return;
    const fallback =
      tenantIds.find((id) => tenants.some((t) => t.id === id)) || tenants[0]?.id || tenantId || "";
    persistSourceTenantId(fallback);
  }, [tenants, sourceTenantId, tenantIds, tenantId, persistSourceTenantId]);

  useEffect(() => {
    if (role !== "superadmin") return;
    setSelected(null);
    setMessages([]);
  }, [role, sourceTenantId]);

  useEffect(() => {
    const t = tenants.find((tenant) => tenant.id === sourceTenantId);
    if (!t) return;
    setSourceDbUrl(t.source_db_url || "");
    setSourceDbType(t.source_db_type || "wordpress");
    const dbType = t.source_db_type || "wordpress";
    const prefixDefaults = applySourceDefaultsForDbType(dbType, {
      tablePrefix: t.source_table_prefix != null ? t.source_table_prefix : undefined,
      urlTable: t.source_url_table != null ? t.source_url_table : undefined,
    });
    setSourceTablePrefix(prefixDefaults.tablePrefix);
    setSourceUrlTable(prefixDefaults.urlTable);
    setSourceMode(normalizeSourceModeForDbType(t.source_db_type || "wordpress", t.source_mode || "wordpress"));
    setSourceStaticUrlsJson(t.source_static_urls_json || "");
    setSourceDomainAliases(t.source_domain_aliases || "");
    setSourceCanonicalBaseUrl(t.source_canonical_base_url || "");
    setBrandName(t.brand_name || "");
    setWidgetPrimaryColor(t.widget_primary_color || "#bf362e");
    setWidgetWebsiteUrl(t.widget_website_url || "");
    setWidgetSourceType(t.widget_source_type || "");
    setWidgetUserMessageColor(t.widget_user_message_color || "#bf362e");
    setWidgetBotMessageColor(t.widget_bot_message_color || "#d5bbb9");
    setWidgetUserMessageTextColor(t.widget_user_message_text_color || "#ffffff");
    setWidgetBotMessageTextColor(t.widget_bot_message_text_color || "#1a1a1a");
    setWidgetHeaderTitle(t.widget_header_title || "");
    setWidgetWelcomeMessage(t.widget_welcome_message || "");
    setChatMaxResults(t.chat_max_results != null && t.chat_max_results !== "" ? String(t.chat_max_results) : "");
    setChatMaxResultsCatalog(
      t.chat_max_results_catalog != null && t.chat_max_results_catalog !== "" ? String(t.chat_max_results_catalog) : "",
    );
    setPrivacyPolicyUrl(t.privacy_policy_url || "");
    setCorsAllowedOrigins(t.cors_allowed_origins || "");
    setClearBrandingAvatar(false);
    setAvatarUpload(null);
    setAvatarPreviewUrl("");
  }, [sourceTenantId, tenants]);

  useEffect(() => {
    if (!avatarUpload) {
      setAvatarPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(avatarUpload);
    setAvatarPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [avatarUpload]);

  useEffect(() => {
    setChatsPage(1);
    setAdminsPage(1);
    setVisitorsPage(1);
    setVisitorChatsPage(1);
    setSelectedVisitorId("");
    setSelectedVisitorSessionId("");
    setVisitorChats([]);
    setSearchQuery("");
    setChatFilter("");
  }, [effectiveTenantId]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    if (section !== "settings" || settingsSubsection !== "security") return;
    if (countriesLoadedRef.current) return;
    countriesLoadedRef.current = true;
    (async () => {
      try {
        const { data } = await client.get("/api/admin/reference/countries");
        setCountryReference(data.countries || []);
      } catch (err) {
        setError(err?.response?.data?.detail || "Failed loading country list");
      }
    })();
  }, [section, settingsSubsection]);

  useEffect(() => {
    const validSections = ["overview", "search", "chats", "jobs", "users", "settings"];
    if (!validSections.includes(section)) {
      navigate("/admin/dashboard/overview", { replace: true });
      return;
    }
    if (section === "search" && !["analytics", "logs", "settings"].includes(searchSubsection)) {
      navigate("/admin/dashboard/search/analytics", { replace: true });
      return;
    }
    if (section === "users" && !["admins", "visitors"].includes(usersSubsection)) {
      navigate("/admin/dashboard/users/admins", { replace: true });
      return;
    }
    if (section === "settings" && !["general", "security", "branding", "moderation", "db-settings", "retrieval-profile"].includes(settingsSubsection)) {
      navigate("/admin/dashboard/settings/general", { replace: true });
      return;
    }
    // Retrieval profile is superadmin-only; bounce admins/managers who reach it by URL.
    if (section === "settings" && settingsSubsection === "retrieval-profile" && role !== "superadmin") {
      navigate("/admin/dashboard/settings/general", { replace: true });
    }
  }, [navigate, section, usersSubsection, settingsSubsection, searchSubsection, role]);

  useEffect(() => {
    if (!effectiveTenantId) return;
    if (section === "overview") {
      loadOverviewSection();
      return;
    }
    if (section === "search") {
      if (searchSubsection === "analytics") {
        loadSearchAnalytics();
      } else if (searchSubsection === "logs") {
        loadSearchLogs(1);
      } else if (searchSubsection === "settings") {
        loadSearchConfig();
      }
      return;
    }
    if (section === "chats") {
      loadChatsSection(chatFilter, chatsPage);
      return;
    }
    if (section === "jobs") {
      loadJobsSection();
      return;
    }
    if (section === "users") {
      if (usersSubsection === "admins") {
        loadAdminsSection(adminsPage);
      } else {
        loadVisitorsSection(visitorsPage);
      }
      return;
    }
    if (section === "settings") {
      if (settingsSubsection === "security" || settingsSubsection === "general") {
        loadSecuritySettings();
        loadRecaptchaSettings();
      } else if (settingsSubsection === "moderation") {
        loadBlockWordSettings();
        loadQuickReplies();
      } else if (settingsSubsection === "retrieval-profile") {
        loadRetrievalProfile();
      }
    }
  }, [
    effectiveTenantId,
    section,
    usersSubsection,
    settingsSubsection,
    searchSubsection,
    chatFilter,
    chatsPage,
    adminsPage,
    visitorsPage,
    loadOverviewSection,
    loadSearchAnalytics,
    loadSearchLogs,
    loadSearchConfig,
    loadChatsSection,
    loadJobsSection,
    loadAdminsSection,
    loadVisitorsSection,
    loadSecuritySettings,
    loadRecaptchaSettings,
    loadBlockWordSettings,
    loadQuickReplies,
    loadRetrievalProfile,
  ]);

  const loadSession = async (id) => {
    setSelected(id);
    setMessages([]);
    const requestSeq = ++sessionRequestRef.current;
    const { data } = await client.get(`/api/admin/chats/${id}`);
    if (requestSeq === sessionRequestRef.current) {
      setMessages(data);
    }
  };

  const reindex = async () => {
    await client.post("/api/reindex", role === "superadmin" ? { tenant_id: sourceTenantId || undefined } : { tenant_id: tenantId });
    await loadJobsSection();
  };

  const loadVisitorChats = useCallback(async (visitorId, page = 1) => {
    setSelectedVisitorId(visitorId);
    setSelectedVisitorSessionId("");
    setSelected(null);
    setMessages([]);
    const { data } = await client.get(`/api/admin/visitors/${visitorId}/chats`, {
      params: { tenant_id: effectiveTenantId, page, page_size: PAGE_SIZE },
    });
    setVisitorChats(data?.items || []);
    setVisitorChatsTotal(Number(data?.total || 0));
    setVisitorChatsPage(Number(data?.page || page));
  }, [effectiveTenantId]);

  useEffect(() => {
    if (!effectiveTenantId || !selectedVisitorId || section !== "users" || usersSubsection !== "visitors") return;
    loadVisitorChats(selectedVisitorId, visitorChatsPage);
  }, [effectiveTenantId, selectedVisitorId, visitorChatsPage, section, usersSubsection, loadVisitorChats]);

  const loadVisitorSession = async (sessionId) => {
    setSelectedVisitorSessionId(sessionId);
    await loadSession(sessionId);
  };

  const createAdmin = async () => {
    try {
      const payload = {
        email: adminEmail,
        password: adminPassword,
        role: newUserRole,
      };
      if (adminTenantMode === "new") {
        payload.new_tenant_name = newTenantName.trim() || undefined;
      } else {
        payload.tenant_id = adminTenantId || undefined;
      }
      await client.post("/api/admin/users", {
        ...payload,
      });
      setSuccess("User created successfully");
      setCreateOpen(false);
      setAdminEmail("");
      setAdminPassword("");
      setAdminTenantId("");
      setAdminTenantMode("existing");
      setNewTenantName("");
      setNewUserRole(role === "admin" ? "manager" : "admin");
      await loadAdminsSection(adminsPage);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create user");
    }
  };

  const updateUserStatus = async (userId, isActive) => {
    try {
      await client.post(`/api/admin/users/${userId}/status`, { is_active: isActive });
      setSuccess(`User ${isActive ? "activated" : "deactivated"} successfully`);
      await loadAdminsSection(adminsPage);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update user status");
    }
  };

  const openReset = (userId) => {
    setResetUserId(userId);
    setNewPassword("");
    setResetOpen(true);
  };

  const resetPassword = async () => {
    try {
      await client.post(`/api/admin/users/${resetUserId}/reset-password`, { new_password: newPassword });
      setSuccess("Password reset successfully");
      setResetOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to reset password");
    }
  };

  const openManageUserTenants = async (user) => {
    try {
      const { data } = await client.get(`/api/admin/users/${user.id}/tenants`);
      const ids = (data?.items || []).map((i) => i.tenant_id);
      setManageTenantUser(user);
      setManageTenantIds(ids);
      setManageTenantsOpen(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load user tenant associations");
    }
  };

  const saveManageUserTenants = async () => {
    if (!manageTenantUser) return;
    setManageTenantSaving(true);
    try {
      await client.put(`/api/admin/users/${manageTenantUser.id}/tenants`, { tenant_ids: manageTenantIds });
      setSuccess("User tenant associations updated");
      setManageTenantsOpen(false);
      setManageTenantUser(null);
      setManageTenantIds([]);
      await loadAdminsSection(adminsPage);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update user tenant associations");
    } finally {
      setManageTenantSaving(false);
    }
  };

  const createStandaloneTenant = async () => {
    const name = newStandaloneTenantName.trim();
    if (!name) return;
    try {
      await client.post("/api/admin/tenants", { name });
      setSuccess("Tenant created");
      setCreateTenantOpen(false);
      setNewStandaloneTenantName("");
      await loadTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create tenant");
    }
  };

  const applySourceDbTypeChange = (nextType) => {
    setSourceDbType(nextType);
    const nextDefaults = applySourceDefaultsForDbType(nextType, {
      tablePrefix: sourceTablePrefix,
      urlTable: sourceUrlTable,
    });
    setSourceTablePrefix(nextDefaults.tablePrefix);
    setSourceUrlTable(nextDefaults.urlTable);
    setSourceMode((prev) => normalizeSourceModeForDbType(nextType, prev));
  };

  const saveSourceConfig = async () => {
    const effectiveMode = normalizeSourceModeForDbType(sourceDbType, sourceMode);
    try {
      await client.patch(`/api/admin/tenants/${sourceTenantId}/source-config`, {
        source_db_url: sourceDbUrl || null,
        source_db_type: sourceDbType || null,
        source_table_prefix: sourceTablePrefix.trim(),
        source_url_table: sourceUrlTable.trim() || null,
        source_mode: effectiveMode || null,
        source_static_urls_json: sourceStaticUrlsJson || null,
        source_domain_aliases: sourceDomainAliases || null,
        source_canonical_base_url: sourceCanonicalBaseUrl || null,
      });
      setSourceMode(effectiveMode);
      setSuccess("Tenant source settings updated");
      await loadTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update source settings");
    }
  };

  const saveBrandingConfig = async () => {
    const parseTenantMaxResults = (raw, label) => {
      const trimmed = String(raw ?? "").trim();
      if (trimmed === "") return { value: null };
      if (!/^\d+$/.test(trimmed)) {
        return { error: `${label} must be a whole number (or leave empty for defaults).` };
      }
      const n = parseInt(trimmed, 10);
      if (n < 1 || n > 50) {
        return { error: `${label} must be between 1 and 50 (matches admin API limits).` };
      }
      return { value: n };
    };
    const generalParsed = parseTenantMaxResults(chatMaxResults, "General chat max results");
    if (generalParsed.error) {
      setError(generalParsed.error);
      return;
    }
    const catalogParsed = parseTenantMaxResults(chatMaxResultsCatalog, "Catalog chat max results");
    if (catalogParsed.error) {
      setError(catalogParsed.error);
      return;
    }
    try {
      const brandingPayload = {
        brand_name: brandName || null,
        widget_primary_color: widgetPrimaryColor || null,
        widget_website_url: widgetWebsiteUrl || null,
        widget_source_type: widgetSourceType || null,
        widget_user_message_color: widgetUserMessageColor || null,
        widget_bot_message_color: widgetBotMessageColor || null,
        widget_user_message_text_color: widgetUserMessageTextColor || null,
        widget_bot_message_text_color: widgetBotMessageTextColor || null,
        widget_header_title: widgetHeaderTitle || null,
        widget_welcome_message: widgetWelcomeMessage || null,
        privacy_policy_url: privacyPolicyUrl || null,
        chat_max_results: generalParsed.value,
        chat_max_results_catalog: catalogParsed.value,
      };
      if (clearBrandingAvatar && !avatarUpload) {
        brandingPayload.avatar_url = "";
      }
      await client.patch(`/api/admin/tenants/${sourceTenantId}/branding`, brandingPayload);
      if (avatarUpload) {
        const fd = new FormData();
        fd.append("file", avatarUpload);
        await client.post(`/api/admin/tenants/${sourceTenantId}/avatar`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAvatarUpload(null);
        setClearBrandingAvatar(false);
      } else if (clearBrandingAvatar) {
        setClearBrandingAvatar(false);
      }
      setSuccess("Branding and widget configuration updated");
      await loadTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update branding settings");
    }
  };

  const testRecaptcha = async () => {
    setRecaptchaTesting(true);
    setRecaptchaStatus(null);
    try {
      const { data } = await client.get(`/api/admin/recaptcha-status`);
      setRecaptchaStatus(data);
    } catch (err) {
      setRecaptchaStatus({ error: err?.response?.data?.detail || "Could not check reCAPTCHA status" });
    } finally {
      setRecaptchaTesting(false);
    }
  };

  const saveRecaptchaSettings = async () => {
    setRecaptchaSaving(true);
    try {
      const body = {
        site_key: recaptchaSiteKeyInput || "",
        min_score: Number(recaptchaMinScore) || 0.5,
      };
      if (recaptchaSecretKeyInput) body.secret_key = recaptchaSecretKeyInput;
      const { data } = await client.put(`/api/admin/recaptcha-settings`, body);
      setRecaptchaSecretSet(Boolean(data?.secret_key_set));
      setRecaptchaSecretKeyInput("");
      setSuccess("reCAPTCHA settings saved");
      await testRecaptcha();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save reCAPTCHA settings");
    } finally {
      setRecaptchaSaving(false);
    }
  };

  const saveSecuritySettings = async () => {
    if (!effectiveTenantId) return;
    try {
      const quotaPayload = {
        quota_reached_message: quotaReachedMessage,
        storefront_search_enabled: storefrontSearchEnabled,
        ai_enabled: aiEnabled,
        transcript_email_enabled: transcriptEmailEnabled,
        transcript_email_to: transcriptEmailTo || null,
      };
      if (role === "superadmin") {
        quotaPayload.monthly_message_limit = Number(monthlyMessageLimit || 15000);
      }
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/quota`, quotaPayload);
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/idle-rating`, {
        idle_rating_wait_seconds: Number(idleRatingWaitSeconds || 120),
      });
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/branding`, {
        cors_allowed_origins: corsAllowedOrigins || null,
        widget_powered_by_text: poweredByText,
      });
      setSuccess("Security settings updated");
      await loadSecuritySettings();
      await loadTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update security settings");
    }
  };

  const addBlockedIp = async () => {
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/blocked-ips`, {
        ip_address: newBlockedIp,
        reason: newBlockedIpReason,
      });
      setNewBlockedIp("");
      setNewBlockedIpReason("");
      await loadSecuritySettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add blocked IP");
    }
  };

  const removeBlockedIp = async (id) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/blocked-ips/${id}`);
      await loadSecuritySettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to remove blocked IP");
    }
  };

  const addBlockedCountry = async () => {
    if (!newBlockedCountry) {
      setError("Select a country to block");
      return;
    }
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/blocked-countries`, {
        country_code: newBlockedCountry,
        reason: newBlockedCountryReason,
      });
      setNewBlockedCountry("");
      setNewBlockedCountryReason("");
      await loadSecuritySettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add blocked country");
    }
  };

  const removeBlockedCountry = async (id) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/blocked-countries/${id}`);
      await loadSecuritySettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to remove blocked country");
    }
  };

  const createBlockWordCategory = async () => {
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/block-word-categories`, {
        name: newCategoryName,
        match_mode: newCategoryMode,
        response_message: newCategoryResponse,
      });
      setNewCategoryName("");
      setNewCategoryMode("exact");
      setNewCategoryResponse("");
      await loadBlockWordSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create category");
    }
  };

  const deleteBlockWordCategory = async (categoryId) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/block-word-categories/${categoryId}`);
      await loadBlockWordSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete category");
    }
  };

  const addWordToCategory = async (categoryId) => {
    const word = (newWordByCategory[categoryId] || "").trim();
    if (!word) return;
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/block-word-categories/${categoryId}/words`, { word });
      setNewWordByCategory((prev) => ({ ...prev, [categoryId]: "" }));
      await loadBlockWordSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add word");
    }
  };

  const deleteWordFromCategory = async (categoryId, wordId) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/block-word-categories/${categoryId}/words/${wordId}`);
      await loadBlockWordSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete word");
    }
  };

  const createQuickReply = async () => {
    if (!newQrTrigger.trim() || !newQrTemplate.trim()) return;
    try {
      const body = {
        category: newQrCategory.trim() || "general",
        trigger_phrase: newQrTrigger.trim(),
        response_template: newQrTemplate.trim(),
        priority: Number(newQrPriority || 0),
        enabled: true,
      };
      const th = newQrThreshold.trim();
      if (th !== "") body.similarity_threshold = Number(th);
      await client.post(`/api/admin/tenants/${effectiveTenantId}/quick-replies`, body);
      setNewQrTrigger("");
      setNewQrTemplate("");
      setNewQrPriority("0");
      setNewQrThreshold("");
      setSuccess("Quick reply added");
      await loadQuickReplies();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add quick reply");
    }
  };

  const deleteQuickReply = async (id) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/quick-replies/${id}`);
      await loadQuickReplies();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete quick reply");
    }
  };

  const setQuickReplyEnabled = async (row, enabled) => {
    try {
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/quick-replies/${row.id}`, { enabled });
      await loadQuickReplies();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update quick reply");
    }
  };

  const updateQuickReplyDraft = (id, patch) => {
    setQuickReplyDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const resetQuickReplyDraft = (row) => {
    setQuickReplyDrafts((prev) => ({ ...prev, [row.id]: { ...row } }));
  };

  const saveQuickReplyDraft = async (row) => {
    const draft = quickReplyDrafts[row.id] || row;
    try {
      const body = {
        category: (draft.category || "general").trim() || "general",
        trigger_phrase: (draft.trigger_phrase || "").trim(),
        response_template: draft.response_template || "",
        priority: Number(draft.priority || 0),
        enabled: !!draft.enabled,
      };
      const th = String(draft.similarity_threshold ?? "").trim();
      if (th !== "") body.similarity_threshold = Number(th);
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/quick-replies/${row.id}`, body);
      setSuccess("Quick reply updated");
      await loadQuickReplies();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save quick reply");
    }
  };

  const isQuickReplyDirty = (row) => {
    const draft = quickReplyDrafts[row.id];
    if (!draft) return false;
    const toNorm = (v) => String(v ?? "").trim();
    return (
      toNorm(draft.category) !== toNorm(row.category) ||
      toNorm(draft.trigger_phrase) !== toNorm(row.trigger_phrase) ||
      toNorm(draft.response_template) !== toNorm(row.response_template) ||
      toNorm(draft.priority) !== toNorm(row.priority) ||
      toNorm(draft.similarity_threshold) !== toNorm(row.similarity_threshold ?? "") ||
      Boolean(draft.enabled) !== Boolean(row.enabled)
    );
  };

  const exportTranscript = () => {
    if (!selected || !messages.length) return;
    const lines = messages.map((m) => `[${m.created_at}] ${m.sender_type.toUpperCase()}: ${m.content}`);
    const blob = new Blob([lines.join("\n\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `session-${selected}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const aggregateFeedback = sessions.reduce(
    (acc, s) => {
      acc.up += Number(s?.feedback_summary?.up || 0);
      acc.down += Number(s?.feedback_summary?.down || 0);
      return acc;
    },
    { up: 0, down: 0 }
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Box
          component="aside"
          sx={{
            width: { xs: "100%", md: 256 },
            flexShrink: 0,
            bgcolor: "background.paper",
            borderRight: { md: "1px solid" },
            borderColor: "divider",
            p: 2,
            position: { md: "sticky" },
            top: { md: 64 },
            alignSelf: "flex-start",
            height: { md: "calc(100vh - 64px)" },
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
              <Stack
                spacing={0.5}
                sx={{
                  "& .MuiButton-root": {
                    justifyContent: "flex-start",
                    width: "100%",
                    px: 1.5,
                    borderRadius: 2,
                    textAlign: "left",
                  },
                }}
              >
                <Button variant={section === "overview" ? "contained" : "text"} onClick={() => navigateSection("overview")}>Overview</Button>
                <Button variant={section === "chats" ? "contained" : "text"} onClick={() => navigateSection("chats")}>Chats</Button>
                <Button variant={section === "jobs" ? "contained" : "text"} onClick={() => navigateSection("jobs")}>Jobs</Button>
                <Button variant={section === "search" ? "contained" : "text"} onClick={() => navigateSection("search/analytics")}>
                  <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    Search
                    <ExpandMoreRoundedIcon
                      fontSize="small"
                      sx={{ transition: "transform .2s", transform: section === "search" ? "rotate(180deg)" : "none" }}
                    />
                  </Box>
                </Button>
                {section === "search" && (
                  <Stack pl={1}>
                    <Button size="small" variant={searchSubsection === "analytics" ? "contained" : "text"} onClick={() => navigateSearchSubsection("analytics")}>Analytics</Button>
                    <Button size="small" variant={searchSubsection === "logs" ? "contained" : "text"} onClick={() => navigateSearchSubsection("logs")}>Query Logs</Button>
                    <Button size="small" variant={searchSubsection === "settings" ? "contained" : "text"} onClick={() => navigateSearchSubsection("settings")}>Configuration</Button>
                  </Stack>
                )}
                <Button variant={section === "users" ? "contained" : "text"} onClick={() => navigateSection("users/admins")}>
                  <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    Users
                    <ExpandMoreRoundedIcon
                      fontSize="small"
                      sx={{ transition: "transform .2s", transform: section === "users" ? "rotate(180deg)" : "none" }}
                    />
                  </Box>
                </Button>
                {section === "users" && (
                  <Stack pl={1}>
                    <Button size="small" variant={usersSubsection === "admins" ? "contained" : "text"} onClick={() => navigateUsersSubsection("admins")}>Admins</Button>
                    <Button size="small" variant={usersSubsection === "visitors" ? "contained" : "text"} onClick={() => navigateUsersSubsection("visitors")}>Visitors</Button>
                  </Stack>
                )}
                <Button variant={section === "settings" ? "contained" : "text"} onClick={() => navigateSection("settings/general")}>
                  <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    Settings
                    <ExpandMoreRoundedIcon
                      fontSize="small"
                      sx={{ transition: "transform .2s", transform: section === "settings" ? "rotate(180deg)" : "none" }}
                    />
                  </Box>
                </Button>
                {section === "settings" && (
                  <Stack pl={1}>
                    <Button size="small" variant={settingsSubsection === "general" ? "contained" : "text"} onClick={() => navigateSettingsSubsection("general")}>General</Button>
                    <Button size="small" variant={settingsSubsection === "security" ? "contained" : "text"} onClick={() => navigateSettingsSubsection("security")}>Security</Button>
                    <Button size="small" variant={settingsSubsection === "branding" ? "contained" : "text"} onClick={() => navigateSettingsSubsection("branding")}>Branding</Button>
                    <Button size="small" variant={settingsSubsection === "moderation" ? "contained" : "text"} onClick={() => navigateSettingsSubsection("moderation")}>Automated Replies</Button>
                    <Button size="small" variant={settingsSubsection === "db-settings" ? "contained" : "text"} onClick={() => navigateSettingsSubsection("db-settings")}>DB Settings</Button>
                    {role === "superadmin" && (
                      <Button size="small" variant={settingsSubsection === "retrieval-profile" ? "contained" : "text"} onClick={() => navigateSettingsSubsection("retrieval-profile")}>Retrieval profile</Button>
                    )}
                  </Stack>
                )}
              </Stack>
              <Box sx={{ mt: "auto", pt: 2 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      fontSize: 16,
                      fontWeight: 700,
                      background: "linear-gradient(135deg,#6366f1,#4338ca)",
                      color: "#fff",
                    }}
                  >
                    {(userEmail || "?").trim().charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      variant="subtitle2"
                      noWrap
                      title={userEmail || ""}
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {userEmail ? userEmail.split("@")[0] : "Signed in"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }} title={userEmail || ""}>
                      {userEmail || ""}
                    </Typography>
                  </Box>
                </Stack>
                {role && (
                  <Chip
                    size="small"
                    label={String(role).replace(/_/g, " ")}
                    sx={{ mt: 1, ml: 0.5, textTransform: "capitalize", fontWeight: 600 }}
                  />
                )}
                {onLogout && (
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<LogoutRoundedIcon fontSize="small" />}
                    onClick={onLogout}
                    sx={{ mt: 1.5, justifyContent: "flex-start", borderColor: "divider" }}
                  >
                    Sign out
                  </Button>
                )}
              </Box>
        </Box>
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4" fontWeight={700}>Admin Dashboard</Typography>
          <Typography color="text.secondary">{role === "superadmin" ? "Global control center" : "Tenant control center"}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {canSelectTenant && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Tenant</InputLabel>
              <Select
                label="Tenant"
                value={sourceTenantId}
                onChange={(e) => persistSourceTenantId(e.target.value)}
              >
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                    {role === "superadmin" ? ` (${t.id})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button variant="outlined" onClick={reindex}>Trigger Reindex</Button>
          {role === "superadmin" && (
            <Button variant="outlined" onClick={() => setCreateTenantOpen(true)}>
              Create Tenant
            </Button>
          )}
          {(role === "superadmin" || role === "admin") && (
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              {role === "superadmin" ? "Add Admin/Manager" : "Add Manager"}
            </Button>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{formatApiError(error)}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {section === "overview" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          {[
            { label: "Total Chats", value: overview?.total_chats || 0, Icon: ChatBubbleOutlineRoundedIcon, color: "#4f46e5", bg: "#eef2ff" },
            { label: "Unique Visitors", value: overview?.unique_visitors || 0, Icon: PeopleAltOutlinedIcon, color: "#0ea5e9", bg: "#e0f2fe" },
            { label: "Embeddings Tokens", value: overview?.embedding_token_usage || 0, Icon: AutoAwesomeOutlinedIcon, color: "#7c3aed", bg: "#f3e8ff" },
            { label: "Chat Tokens", value: overview?.chat_token_usage || 0, Icon: BoltOutlinedIcon, color: "#d97706", bg: "#fef3c7" },
            { label: "Likes", value: overview?.likes || 0, Icon: ThumbUpAltOutlinedIcon, color: "#16a34a", bg: "#dcfce7" },
            { label: "Dislikes", value: overview?.dislikes || 0, Icon: ThumbDownAltOutlinedIcon, color: "#dc2626", bg: "#fee2e2" },
            { label: "Session Ratings", value: overview?.rating_count || 0, Icon: StarBorderRoundedIcon, color: "#0d9488", bg: "#ccfbf1" },
            { label: "Average Rating", value: overview?.average_rating || 0, Icon: GradeRoundedIcon, color: "#ca8a04", bg: "#fef9c3" },
          ].map((m) => {
            const Icon = m.Icon;
            const display = typeof m.value === "number" && !Number.isInteger(m.value)
              ? m.value
              : Number(m.value).toLocaleString();
            return (
              <Card key={m.label}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                    <Typography
                      variant="caption"
                      sx={{ textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: "text.secondary", mt: 0.5 }}
                    >
                      {m.label}
                    </Typography>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: m.bg,
                        color: m.color,
                        flexShrink: 0,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                  </Stack>
                  <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                    {display}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {section === "search" && searchSubsection === "analytics" && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Search Analytics</Typography>
              <Typography variant="body2" color="text.secondary">How customers are using AI search.</Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Period</InputLabel>
                <Select label="Period" value={searchDays} onChange={(e) => setSearchDays(e.target.value)}>
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Channel</InputLabel>
                <Select label="Channel" value={searchChannel} onChange={(e) => setSearchChannel(e.target.value)}>
                  <MenuItem value="">All channels</MenuItem>
                  <MenuItem value="storefront_search">Storefront search</MenuItem>
                  <MenuItem value="chat">Chat assistant</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={exportSearchCsv}>Export CSV</Button>
            </Stack>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2, mb: 2 }}>
            {[
              { label: "Total Searches", value: searchAnalytics?.totals?.total_searches || 0, Icon: SearchRoundedIcon, color: "#4f46e5", bg: "#eef2ff" },
              { label: "Unique Queries", value: searchAnalytics?.totals?.unique_queries || 0, Icon: QueryStatsRoundedIcon, color: "#0ea5e9", bg: "#e0f2fe" },
              { label: "Zero-Result", value: searchAnalytics?.totals?.zero_results || 0, sub: `${searchAnalytics?.totals?.zero_result_rate || 0}% of searches`, Icon: ErrorOutlineRoundedIcon, color: "#dc2626", bg: "#fee2e2" },
              { label: "Avg Results", value: searchAnalytics?.totals?.avg_results || 0, Icon: AutoAwesomeOutlinedIcon, color: "#7c3aed", bg: "#f3e8ff" },
              { label: "Result Clicks", value: searchAnalytics?.totals?.total_clicks || 0, sub: `${searchAnalytics?.totals?.click_through_rate || 0}% CTR`, Icon: TouchAppOutlinedIcon, color: "#d97706", bg: "#fef3c7" },
              { label: "Conversions", value: searchAnalytics?.totals?.conversions || 0, sub: `${searchAnalytics?.totals?.conversion_rate || 0}% rate`, Icon: ShoppingCartOutlinedIcon, color: "#16a34a", bg: "#dcfce7" },
              { label: "Unique Searchers", value: searchAnalytics?.totals?.unique_searchers || 0, Icon: PeopleAltOutlinedIcon, color: "#0d9488", bg: "#ccfbf1" },
            ].map((m) => {
              const Icon = m.Icon;
              const display = typeof m.value === "number" && !Number.isInteger(m.value) ? m.value : Number(m.value).toLocaleString();
              return (
                <Card key={m.label}>
                  <CardContent>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                      <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: "text.secondary", mt: 0.5 }}>{m.label}</Typography>
                      <Box sx={{ width: 38, height: 38, borderRadius: 2.5, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: m.bg, color: m.color, flexShrink: 0 }}>
                        <Icon fontSize="small" />
                      </Box>
                    </Stack>
                    <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{display}</Typography>
                    {m.sub && <Typography variant="caption" color="text.secondary">{m.sub}</Typography>}
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                <TrendingUpRoundedIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Search usage trend</Typography>
              </Stack>
              {(searchAnalytics?.trend || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No search activity in this period yet.</Typography>
              ) : (
                <Stack spacing={0.75}>
                  {(() => {
                    const max = Math.max(1, ...searchAnalytics.trend.map((t) => t.searches));
                    return searchAnalytics.trend.map((t) => (
                      <Stack key={t.date} direction="row" alignItems="center" gap={1.5}>
                        <Typography variant="caption" sx={{ width: 92, color: "text.secondary", flexShrink: 0 }}>{t.date}</Typography>
                        <Box sx={{ flexGrow: 1, bgcolor: "#f1f5f9", borderRadius: 1, height: 18, position: "relative", overflow: "hidden" }}>
                          <Box sx={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `${(t.searches / max) * 100}%`, background: "linear-gradient(90deg,#6366f1,#4f46e5)", borderRadius: 1 }} />
                        </Box>
                        <Typography variant="caption" sx={{ width: 96, textAlign: "right", flexShrink: 0 }}>
                          {t.searches}{t.zero_results ? ` · ${t.zero_results} zero` : ""}
                        </Typography>
                      </Stack>
                    ));
                  })()}
                </Stack>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0,1fr))" }, gap: 2 }}>
            {[
              { title: "Top searched keywords", rows: searchAnalytics?.top_keywords, primary: (r) => r.query, secondary: (r) => `avg ${r.avg_results} results`, value: (r) => r.count },
              { title: "Zero-result searches", rows: searchAnalytics?.zero_result_keywords, primary: (r) => r.query, secondary: null, value: (r) => r.count },
              { title: "Low-performing terms", rows: searchAnalytics?.low_performing, primary: (r) => r.query, secondary: (r) => `avg ${r.avg_results} results`, value: (r) => r.count },
              { title: "Most clicked products", rows: searchAnalytics?.clicked_products, primary: (r) => r.product_name || r.product_id, secondary: (r) => r.product_id, value: (r) => r.clicks },
            ].map((card) => {
              const rows = card.rows || [];
              return (
                <Card key={card.title}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>{card.title}</Typography>
                    {rows.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No data yet.</Typography>
                    ) : (
                      <Stack spacing={0}>
                        {rows.map((r, i) => (
                          <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75, borderBottom: i < rows.length - 1 ? "1px solid" : "none", borderColor: "divider" }}>
                            <Box sx={{ minWidth: 0, mr: 1 }}>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{card.primary(r)}</Typography>
                              {card.secondary && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>{card.secondary(r)}</Typography>}
                            </Box>
                            <Chip size="small" label={Number(card.value(r)).toLocaleString()} sx={{ flexShrink: 0 }} />
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      {section === "search" && searchSubsection === "logs" && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Search Query Logs</Typography>
            <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={exportSearchCsv}>Export CSV</Button>
          </Stack>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <TextField
                  size="small"
                  label="Search query text"
                  value={searchLogQuery}
                  onChange={(e) => { setSearchLogQuery(e.target.value); searchLogQueryRef.current = e.target.value; }}
                  onKeyDown={(e) => { if (e.key === "Enter") loadSearchLogs(1); }}
                  sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Channel</InputLabel>
                  <Select label="Channel" value={searchChannel} onChange={(e) => setSearchChannel(e.target.value)}>
                    <MenuItem value="">All channels</MenuItem>
                    <MenuItem value="storefront_search">Storefront search</MenuItem>
                    <MenuItem value="chat">Chat assistant</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Switch checked={searchLogZeroOnly} onChange={(e) => setSearchLogZeroOnly(e.target.checked)} />}
                  label="Zero-result only"
                />
                <Button variant="contained" onClick={() => loadSearchLogs(1)}>Apply</Button>
              </Stack>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 150px 90px 180px", gap: 1, px: 1, py: 1, fontWeight: 700, color: "text.secondary", borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>QUERY</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>CHANNEL</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, textAlign: "right" }}>RESULTS</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, textAlign: "right" }}>WHEN</Typography>
              </Box>
              {searchLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No search queries logged yet.</Typography>
              ) : (
                searchLogs.map((row) => (
                  <Box key={row.id} sx={{ display: "grid", gridTemplateColumns: "1fr 150px 90px 180px", gap: 1, px: 1, py: 1, alignItems: "center", borderBottom: "1px solid", borderColor: "divider" }}>
                    <Typography variant="body2" noWrap title={row.query}>{row.query}</Typography>
                    <Chip size="small" variant="outlined" label={row.channel === "chat" ? "Chat" : "Storefront"} />
                    <Box sx={{ textAlign: "right" }}>
                      {row.zero_result
                        ? <Chip size="small" color="error" label="0" />
                        : <Typography variant="body2">{row.result_count}</Typography>}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
                      {new Date(row.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
          {searchLogsTotal > PAGE_SIZE && (
            <Stack alignItems="center" mt={2}>
              <Pagination
                count={Math.ceil(searchLogsTotal / PAGE_SIZE)}
                page={searchLogsPage}
                onChange={(e, p) => loadSearchLogs(p)}
              />
            </Stack>
          )}
        </Box>
      )}

      {section === "search" && searchSubsection === "settings" && (
        <Box sx={{ display: "grid", gap: 2, maxWidth: 940 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Search Configuration</Typography>
            <Typography variant="body2" color="text.secondary">Control how AI search behaves for this store.</Typography>
          </Box>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>General Configuration</Typography>
              <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
                <Chip size="small" color={searchConfig?.ai_enabled ? "success" : "default"} label={`AI Search: ${searchConfig?.ai_enabled ? "On" : "Off"}`} />
                <Chip size="small" color={searchConfig?.storefront_search_enabled ? "success" : "default"} label={`Storefront search: ${searchConfig?.storefront_search_enabled ? "On" : "Off"}`} />
                <Button size="small" onClick={() => navigateSettingsSubsection("general")}>Manage in General</Button>
              </Stack>
              <FormControlLabel
                sx={{ display: "flex", m: 0, mb: 1.5, justifyContent: "space-between" }}
                labelPlacement="start"
                control={
                  <Switch
                    checked={!!searchConfig?.auto_sync_enabled}
                    onChange={() => saveSearchConfig({ auto_sync_enabled: !searchConfig?.auto_sync_enabled })}
                  />
                }
                label={<Box><Typography sx={{ fontWeight: 600 }}>Auto Sync</Typography><Typography variant="caption" color="text.secondary">Automatically keep the search index in sync with the store.</Typography></Box>}
              />
              <Divider sx={{ my: 1.5 }} />
              {/* Align by the top so the input boxes line up regardless of how many
                  lines each field's helper text wraps to; the Save button is nudged
                  down to sit level with the inputs. */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
                {searchConfig?.supports_store_view && (
                  <TextField
                    size="small"
                    label="Store View ID"
                    helperText="Magento store_id (e.g. 1)"
                    value={searchConfig?.store_view ?? ""}
                    onChange={(e) => setCfgField("store_view", e.target.value)}
                    sx={{ maxWidth: 200 }}
                  />
                )}
                <TextField
                  size="small"
                  type="number"
                  label="Search Result Limit"
                  helperText="Max products per search (1–50)"
                  value={searchConfig?.search_result_limit ?? ""}
                  onChange={(e) => setCfgField("search_result_limit", e.target.value)}
                  inputProps={{ min: 1, max: 50 }}
                  sx={{ maxWidth: 200 }}
                />
                <Button
                  variant="contained"
                  disabled={searchConfigSaving}
                  sx={{ mt: { sm: "4px" } }}
                  onClick={() => saveSearchConfig({
                    ...(searchConfig?.supports_store_view ? { store_view: searchConfig?.store_view || "" } : {}),
                    search_result_limit: searchConfig?.search_result_limit ? Number(searchConfig.search_result_limit) : undefined,
                  })}
                >
                  Save
                </Button>
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>API Key Configuration</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                External AI (OpenAI) key for this store. Leave blank to use the platform key.
                {searchConfig?.api_key_set ? ` Currently set (${searchConfig?.api_key_hint || "configured"}).` : " Not set."}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }}>
                <TextField
                  size="small"
                  type="password"
                  label="API key"
                  placeholder={searchConfig?.api_key_set ? "•••• set ••••" : "sk-…"}
                  value={searchConfig?.openai_api_key_input ?? ""}
                  onChange={(e) => setCfgField("openai_api_key_input", e.target.value)}
                  sx={{ maxWidth: 320 }}
                />
                <Button
                  variant="outlined"
                  disabled={searchConfigSaving || !(searchConfig?.openai_api_key_input || "").trim()}
                  onClick={() => saveSearchConfig({ openai_api_key: (searchConfig?.openai_api_key_input || "").trim() })}
                >
                  Save key
                </Button>
                {searchConfig?.api_key_set && (
                  <Button
                    color="error"
                    disabled={searchConfigSaving}
                    onClick={() => saveSearchConfig({ openai_api_key: "" })}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                <StorageRoundedIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Search Index Status</Typography>
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" }, gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 700 }}>Indexed Products</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{searchIndexStatus?.indexed_products != null ? Number(searchIndexStatus.indexed_products).toLocaleString() : "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 700 }}>Index</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip size="small" color={searchIndexStatus?.collection_exists ? "success" : "warning"} label={searchIndexStatus?.collection_exists ? "Ready" : "Not built"} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 700 }}>Last Reindex</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {searchIndexStatus?.last_reindex
                      ? <Chip size="small" color={searchIndexStatus.last_reindex.status === "completed" ? "success" : searchIndexStatus.last_reindex.status === "failed" ? "error" : "default"} label={searchIndexStatus.last_reindex.status} />
                      : <Typography variant="body2" color="text.secondary">Never</Typography>}
                  </Box>
                </Box>
              </Box>
              {searchIndexStatus?.last_reindex?.finished_at && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Last {searchIndexStatus?.last_reindex?.mode || "full"} reindex finished {new Date(searchIndexStatus.last_reindex.finished_at).toLocaleString()}
                </Typography>
              )}
              {searchIndexStatus?.health && (
                <Box sx={{ mb: 1.5 }}>
                  <Chip
                    size="small"
                    color={searchIndexStatus.health.healthy ? "success" : "warning"}
                    label={searchIndexStatus.health.healthy ? "Index healthy" : `Issues: ${(searchIndexStatus.health.issues || []).join(", ").replace(/_/g, " ")}`}
                  />
                  {searchIndexStatus?.bucket_counts && Object.keys(searchIndexStatus.bucket_counts).length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {Object.entries(searchIndexStatus.bucket_counts).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </Typography>
                  )}
                </Box>
              )}
              <Stack direction="row" spacing={1.5}>
                <Button variant="contained" onClick={() => runReindexFromSearch("full")}>Run Full Reindex</Button>
                <Button variant="outlined" onClick={() => runReindexFromSearch("incremental")}>Run Incremental Reindex</Button>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Autocomplete</Typography>
              {[
                { key: "enabled", label: "Enable autocomplete suggestions" },
                { key: "show_products", label: "Show products in suggestions" },
                { key: "show_categories", label: "Show categories in suggestions" },
                { key: "show_popular", label: "Show popular searches" },
                { key: "show_recent", label: "Show recent searches" },
              ].map((opt) => (
                <FormControlLabel
                  key={opt.key}
                  sx={{ display: "flex", m: 0, justifyContent: "space-between" }}
                  labelPlacement="start"
                  control={
                    <Switch
                      checked={!!searchConfig?.autocomplete?.[opt.key]}
                      onChange={() => saveSearchConfig({ autocomplete: { [opt.key]: !searchConfig?.autocomplete?.[opt.key] } })}
                    />
                  }
                  label={opt.label}
                />
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <TextField
                  size="small"
                  type="number"
                  label="Suggestion count"
                  value={searchConfig?.autocomplete?.suggestion_count ?? 6}
                  onChange={(e) => setAutocompleteField("suggestion_count", e.target.value)}
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ maxWidth: 180 }}
                />
                <Button
                  variant="outlined"
                  disabled={searchConfigSaving}
                  onClick={() => saveSearchConfig({ autocomplete: { suggestion_count: Number(searchConfig?.autocomplete?.suggestion_count || 6) } })}
                >
                  Save
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Zero-Result Handling</Typography>
              {[
                { key: "suggest_alternatives", label: "Suggest alternative keywords" },
                { key: "show_related", label: "Show related products" },
                { key: "show_trending", label: "Show trending products" },
                { key: "show_popular_categories", label: "Show popular categories" },
              ].map((opt) => (
                <FormControlLabel
                  key={opt.key}
                  sx={{ display: "flex", m: 0, justifyContent: "space-between" }}
                  labelPlacement="start"
                  control={
                    <Switch
                      checked={!!searchConfig?.zero_result?.[opt.key]}
                      onChange={() => saveSearchConfig({ zero_result: { [opt.key]: !searchConfig?.zero_result?.[opt.key] } })}
                    />
                  }
                  label={opt.label}
                />
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Redirect rules for failed searches</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                When a search matches a keyword, send the shopper straight to this URL (e.g. send "gift card" to your gift-card page).
              </Typography>
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                {(searchConfig?.zero_result?.redirects || []).map((r, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={r.keyword} />
                    <Typography variant="body2" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} color="text.secondary">{r.url}</Typography>
                    <Button
                      size="small"
                      color="error"
                      disabled={searchConfigSaving}
                      onClick={() => saveSearchConfig({ zero_result: { redirects: (searchConfig?.zero_result?.redirects || []).filter((_, j) => j !== i) } })}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-end" }}>
                <TextField size="small" label="Keyword" value={searchConfig?.redirect_kw_input ?? ""} onChange={(e) => setCfgField("redirect_kw_input", e.target.value)} sx={{ maxWidth: 180 }} />
                <TextField size="small" label="Redirect URL" value={searchConfig?.redirect_url_input ?? ""} onChange={(e) => setCfgField("redirect_url_input", e.target.value)} sx={{ flex: 1 }} />
                <Button
                  variant="outlined"
                  disabled={searchConfigSaving || !(searchConfig?.redirect_kw_input || "").trim() || !(searchConfig?.redirect_url_input || "").trim()}
                  onClick={() => saveSearchConfig({ zero_result: { redirects: [
                    ...(searchConfig?.zero_result?.redirects || []),
                    { keyword: (searchConfig?.redirect_kw_input || "").trim(), url: (searchConfig?.redirect_url_input || "").trim() },
                  ] } })}
                >
                  Add rule
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

      {section === "chats" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(420px, 1fr) minmax(420px, 1fr)" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Card sx={{ minWidth: 0 }}>
            <CardContent>
              <Stack direction="row" spacing={1} mb={1} alignItems="center">
                <Typography variant="h6">Tenant Chats</Typography>
                <Chip size="small" label={`👍 ${aggregateFeedback.up}`} />
                <Chip size="small" label={`👎 ${aggregateFeedback.down}`} />
                <Chip size="small" label={`Embeddings Tokens ${usageSummary?.embedding_total_tokens || 0}`} />
              </Stack>
              <TextField
                size="small"
                label="Search chats"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setChatFilter(e.currentTarget.value);
                    setChatsPage(1);
                    loadChatsSection(e.currentTarget.value, 1);
                  }
                }}
                fullWidth
                sx={{ mb: 1 }}
              />
              <List dense sx={{ maxHeight: 520, overflowY: "auto" }}>
                {sessions.map((s) => (
                  <ListItemButton key={s.id} selected={selected === s.id} onClick={() => loadSession(s.id)}>
                    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ width: "100%", minWidth: 0 }}>
                      <ListItemText
                        sx={{ flex: 1, minWidth: 0 }}
                        primary={s.title || s.id}
                        secondary={`${s.visitor_name || "Unknown"}${s.visitor_email ? ` (${s.visitor_email})` : ""} | ${s.id} | ${s.last_message_at} | 👍 ${s?.feedback_summary?.up || 0} 👎 ${s?.feedback_summary?.down || 0}`}
                        secondaryTypographyProps={{ sx: { wordBreak: "break-word" } }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, pt: 0.25 }}>
                        {Number(s.message_count ?? 0)} msgs
                      </Typography>
                    </Stack>
                  </ListItemButton>
                ))}
              </List>
              <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
                <Pagination
                  count={Math.max(1, Math.ceil(chatsTotal / PAGE_SIZE))}
                  page={chatsPage}
                  onChange={(_, value) => setChatsPage(value)}
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 0 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Conversation</Typography>
                <Button size="small" onClick={exportTranscript} disabled={!selected}>Export</Button>
              </Stack>
              {selected ? (
                <Stack spacing={1.5} sx={{ maxHeight: 560, overflowY: "auto" }}>
                  {messages.map((m) => (
                    <Box key={m.id} sx={{ p: 1.5, borderRadius: 1, bgcolor: m.sender_type === "assistant" ? "grey.100" : "primary.50" }}>
                      <Typography variant="caption" color="text.secondary">{m.sender_type}</Typography>
                      <Typography sx={{ wordBreak: "break-word" }}>{m.content}</Typography>
                      {m.sender_type === "assistant" && (
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                          <Chip size="small" label={`Prompt ${m?.token_usage?.prompt_tokens || 0}`} />
                          <Chip size="small" label={`Completion ${m?.token_usage?.completion_tokens || 0}`} />
                          <Chip size="small" label={`Total ${m?.token_usage?.total_tokens || 0}`} />
                        </Stack>
                      )}
                      {m.sender_type === "assistant" && (
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                          <Chip size="small" label={`👍 ${m?.feedback_summary?.up || 0}`} />
                          <Chip size="small" label={`👎 ${m?.feedback_summary?.down || 0}`} />
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">Select a chat session.</Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {section === "jobs" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Reindex Jobs</Typography>
            <Stack spacing={1}>
              {jobs.map((j) => (
                <Card key={j.id} variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Typography sx={{ minWidth: 320 }} fontWeight={600}>{j.id}</Typography>
                      {j.tenant_name && <Chip size="small" label={j.tenant_name} />}
                      <Chip size="small" label={j.scope} />
                      <Chip size="small" color={j.status === "completed" ? "success" : j.status === "failed" ? "error" : "default"} label={j.status} />
                    </Stack>
                    {(() => {
                      const prog = reindexJobProgress(j);
                      if (!prog || j.status === "completed" || j.status === "failed") return null;
                      return (
                      <Box sx={{ mb: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={prog.percentage}
                          sx={{ height: 8, borderRadius: 10, mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {prog.processed}/{prog.total} processed ({prog.percentage.toFixed(1)}%)
                          {prog.remaining > 0 ? ` · ${prog.remaining} remaining` : ""}
                        </Typography>
                      </Box>
                      );
                    })()}
                    <Typography variant="caption" color="text.secondary" display="block">
                      Started: {j.started_at || "N/A"} {j.finished_at ? `| Finished: ${j.finished_at}` : ""}
                    </Typography>
                    {!!j.error && (
                      <Typography variant="caption" color="error.main" display="block">
                        Error: {j.error}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {section === "users" && usersSubsection === "admins" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Admins</Typography>
            <Stack spacing={1}>
              {users.map((u) => (
                <Stack key={u.id} direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ minWidth: 280 }}>{u.email}</Typography>
                  <Chip size="small" label={u.role} color={u.role === "superadmin" ? "secondary" : "primary"} />
                  <Chip size="small" label={u.is_active ? "active" : "inactive"} />
                  {u.role !== "superadmin" && (
                    <>
                      <Button size="small" onClick={() => updateUserStatus(u.id, !u.is_active)}>
                        {u.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button size="small" onClick={() => openReset(u.id)}>Reset Password</Button>
                      {(role === "superadmin" || (role === "admin" && u.role === "manager")) && (
                        <Button size="small" onClick={() => openManageUserTenants(u)}>
                          Manage Tenants
                        </Button>
                      )}
                    </>
                  )}
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
              <Pagination
                count={Math.max(1, Math.ceil(adminsTotal / PAGE_SIZE))}
                page={adminsPage}
                onChange={(_, value) => setAdminsPage(value)}
                size="small"
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {section === "users" && usersSubsection === "visitors" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))", lg: "320px minmax(0, 1fr) minmax(0, 1.3fr)" },
            gap: 2,
            minHeight: { md: "calc(100vh - 220px)" },
            alignItems: "stretch",
          }}
        >
          <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
                  <Typography variant="h6">Visitors</Typography>
                  <Chip size="small" variant="outlined" label={`${visitorsTotal} total`} />
                </Stack>
                <List dense>
                  {visitors.map((v) => (
                    <ListItemButton
                      key={v.visitor_id}
                      selected={selectedVisitorId === v.visitor_id}
                      onClick={() => {
                        setVisitorChatsPage(1);
                        loadVisitorChats(v.visitor_id, 1);
                      }}
                    >
                      <ListItemText
                        primary={`${v.name || "Unknown"}${v.email ? ` (${v.email})` : ""}`}
                        secondary={`Chats: ${v.chat_count} | Last seen: ${v.last_seen_at || "N/A"}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
                <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
                  <Pagination
                    count={Math.max(1, Math.ceil(visitorsTotal / PAGE_SIZE))}
                    page={visitorsPage}
                    onChange={(_, value) => setVisitorsPage(value)}
                    size="small"
                  />
                </Stack>
              </CardContent>
            </Card>
          <Card>
              <CardContent>
                <Typography variant="h6" mb={1}>Visitor Chats</Typography>
                {selectedVisitorId ? (
                  <>
                    <List dense>
                      {visitorChats.map((s) => (
                        <ListItemButton
                          key={s.id}
                          selected={selectedVisitorSessionId === s.id}
                          onClick={() => loadVisitorSession(s.id)}
                        >
                          <ListItemText
                            primary={s.title || s.id}
                            secondary={`${s.last_message_at || "N/A"} | 👍 ${s?.feedback_summary?.up || 0} 👎 ${s?.feedback_summary?.down || 0}`}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                    <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
                      <Pagination
                        count={Math.max(1, Math.ceil(visitorChatsTotal / PAGE_SIZE))}
                        page={visitorChatsPage}
                        onChange={(_, value) => setVisitorChatsPage(value)}
                        size="small"
                      />
                    </Stack>
                  </>
                ) : (
                  <Typography color="text.secondary">Select a user to view chats.</Typography>
                )}
              </CardContent>
            </Card>
          <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">Conversation</Typography>
                  <Button size="small" onClick={exportTranscript} disabled={!selectedVisitorSessionId}>Export</Button>
                </Stack>
                {!selectedVisitorId ? (
                  <Typography color="text.secondary">Select a visitor first.</Typography>
                ) : !selectedVisitorSessionId ? (
                  <Typography color="text.secondary">Select a visitor chat to view transcript.</Typography>
                ) : (
                  <Stack spacing={1.5} sx={{ maxHeight: 560, overflowY: "auto" }}>
                    {messages.map((m) => (
                      <Box key={m.id} sx={{ p: 1.5, borderRadius: 1, bgcolor: m.sender_type === "assistant" ? "grey.100" : "primary.50" }}>
                        <Typography variant="caption" color="text.secondary">{m.sender_type}</Typography>
                        <Typography sx={{ wordBreak: "break-word" }}>{m.content}</Typography>
                        {m.sender_type === "assistant" && (
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                            <Chip size="small" label={`Prompt ${m?.token_usage?.prompt_tokens || 0}`} />
                            <Chip size="small" label={`Completion ${m?.token_usage?.completion_tokens || 0}`} />
                            <Chip size="small" label={`Total ${m?.token_usage?.total_tokens || 0}`} />
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
        </Box>
      )}

      {section === "settings" && settingsSubsection === "general" && (
        <Stack spacing={2}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: aiEnabled ? "success.main" : "error.main",
            borderLeft: "6px solid",
            borderLeftColor: aiEnabled ? "success.main" : "error.main",
            bgcolor: aiEnabled ? "rgba(46,125,50,0.06)" : "rgba(211,47,47,0.06)",
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="h6">AI Assistant</Typography>
                  <Chip
                    size="small"
                    label={aiEnabled ? "ACTIVE" : "DISABLED"}
                    color={aiEnabled ? "success" : "error"}
                    variant={aiEnabled ? "filled" : "outlined"}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Master switch for this tenant. When off, the chat assistant <b>and</b> storefront search are fully disabled — nothing loads on the customer's site and all AI requests are refused.
                </Typography>
              </Box>
              <Switch
                checked={aiEnabled}
                onChange={(e) => setAiEnabled(e.target.checked)}
                sx={{ transform: "scale(1.3)", mr: 1 }}
              />
            </Stack>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Widget Embed Code</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Paste this snippet before the closing &lt;/body&gt; tag on the customer site. The site origin must be listed in CORS Allowed Origins (under Security).
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Embed snippet"
                value={embedConfig.snippet}
                fullWidth
                multiline
                minRows={10}
                InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 13 } }}
              />
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  onClick={regenerateWidgetKey}
                  disabled={!effectiveTenantId || keyBusy}
                >
                  {keyBusy
                    ? "Working…"
                    : embedConfig.tenantPublicKey
                      ? "Regenerate key"
                      : "Generate key"}
                </Button>
                <Button variant="contained" onClick={copyEmbedSnippet} disabled={!selectedTenant}>
                  {embedCopied ? "Copied!" : "Copy snippet"}
                </Button>
                {!embedConfig.tenantPublicKey && (
                  <Typography variant="caption" color="error">
                    Snippet uses a placeholder key until you generate one.
                  </Typography>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Widget Footer</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Text shown at the bottom of the chat widget (e.g. “Powered by Company Name”). Leave it empty to hide the footer.
            </Typography>
            <Stack spacing={2} sx={{ maxWidth: 480 }}>
              <TextField
                label="Footer text"
                placeholder="Powered by Company Name"
                value={poweredByText}
                onChange={(e) => setPoweredByText(e.target.value)}
                fullWidth
                helperText="Leave empty to hide the footer in the widget."
              />
              <Box>
                <Button variant="contained" onClick={saveSecuritySettings} disabled={!effectiveTenantId}>
                  Save settings
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Storefront Search</Typography>
            <Stack spacing={2}>
              <FormControlLabel
                disabled={!aiEnabled}
                control={
                  <Switch
                    checked={storefrontSearchEnabled && aiEnabled}
                    onChange={(e) => setStorefrontSearchEnabled(e.target.checked)}
                  />
                }
                label={
                  aiEnabled
                    ? "Enable storefront search (route the site search box to the chat assistant)"
                    : "Enable storefront search — unavailable while AI Assistant is disabled"
                }
              />
              {storefrontSearchEnabled && (
                <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "grey.50" }}>
                  <Typography variant="subtitle2" mb={0.5}>Storefront search install snippet</Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Add this to your storefront theme on every page, after the chatbot widget embed. Choose your platform to set the correct selectors.
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
                    <InputLabel id="storefront-platform-label">Platform</InputLabel>
                    <Select
                      labelId="storefront-platform-label"
                      label="Platform"
                      value={storefrontPlatform}
                      onChange={(e) => setStorefrontPlatform(e.target.value)}
                    >
                      {Object.entries(STOREFRONT_SEARCH_PLATFORMS).map(([key, p]) => (
                        <MenuItem key={key} value={key}>{p.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    value={searchSnippet}
                    fullWidth
                    multiline
                    minRows={12}
                    InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
                  />
                  <Stack direction="row" spacing={1} mt={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button variant="contained" onClick={copySearchSnippet}>
                      {searchSnippetCopied ? "Copied!" : "Copy snippet"}
                    </Button>
                    {storefrontPlatform !== "magento" && (
                      <Typography variant="caption" color="text.secondary">
                        Verify SEARCH_INPUT matches your theme's search box; set NATIVE_DROPDOWN only if your theme shows its own autocomplete.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}
              <Button variant="contained" onClick={saveSecuritySettings} disabled={!effectiveTenantId}>
                Save settings
              </Button>
            </Stack>
          </CardContent>
        </Card>
        </Stack>
      )}

      {section === "settings" && settingsSubsection === "security" && (
        <Stack spacing={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Tenant Security and Quota Controls</Typography>
            <Stack spacing={2}>
              <TextField
                label="Monthly Visitor Message Limit"
                type="number"
                value={monthlyMessageLimit}
                onChange={(e) => setMonthlyMessageLimit(e.target.value)}
                disabled={role !== "superadmin"}
                sx={{ maxWidth: 360 }}
              />
              <TextField
                label="Quota Reached Message"
                value={quotaReachedMessage}
                onChange={(e) => setQuotaReachedMessage(e.target.value)}
                fullWidth
              />
              <TextField
                label="CORS Allowed Origins (comma-separated)"
                value={corsAllowedOrigins}
                onChange={(e) => setCorsAllowedOrigins(e.target.value)}
                fullWidth
              />
              <TextField
                label="Idle Rating Wait (seconds)"
                type="number"
                value={idleRatingWaitSeconds}
                onChange={(e) => setIdleRatingWaitSeconds(e.target.value)}
                sx={{ maxWidth: 360 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={transcriptEmailEnabled}
                    onChange={(e) => setTranscriptEmailEnabled(e.target.checked)}
                  />
                }
                label="Email chat transcript to admin when a visitor closes the chat"
              />
              <TextField
                label="Transcript recipient email"
                type="email"
                value={transcriptEmailTo}
                onChange={(e) => setTranscriptEmailTo(e.target.value)}
                placeholder="admin@yourdomain.com"
                helperText="Where transcripts are sent on chat close. Requires SMTP configured on the server (SMTP_HOST)."
                fullWidth
                disabled={!transcriptEmailEnabled}
                sx={{ maxWidth: 480 }}
              />
              <Button variant="contained" onClick={saveSecuritySettings} disabled={!effectiveTenantId}>
                Save security settings
              </Button>
              {role === "superadmin" && (
                <Box sx={{ border: "1px solid #eee", borderRadius: 1, p: 2, mt: 1, display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 520 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    reCAPTCHA v3 (bot protection on the name/email gate)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Global keys for all tenants. Register the key for the widget-app domain in the
                    Google reCAPTCHA v3 console. When both keys are set, it's enforced automatically.
                  </Typography>
                  <TextField
                    label="Site key"
                    value={recaptchaSiteKeyInput}
                    onChange={(e) => setRecaptchaSiteKeyInput(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={recaptchaSecretSet ? "Secret key (stored — leave blank to keep)" : "Secret key"}
                    type="password"
                    value={recaptchaSecretKeyInput}
                    onChange={(e) => setRecaptchaSecretKeyInput(e.target.value)}
                    placeholder={recaptchaSecretSet ? "•••••••• (stored)" : ""}
                    fullWidth
                  />
                  <TextField
                    label="Min score (0–1, higher = stricter)"
                    type="number"
                    inputProps={{ step: 0.1, min: 0, max: 1 }}
                    value={recaptchaMinScore}
                    onChange={(e) => setRecaptchaMinScore(e.target.value)}
                    sx={{ maxWidth: 220 }}
                  />
                  <Stack direction="row" gap={1}>
                    <Button variant="contained" size="small" onClick={saveRecaptchaSettings} disabled={recaptchaSaving}>
                      {recaptchaSaving ? "Saving…" : "Save reCAPTCHA keys"}
                    </Button>
                    <Button variant="outlined" size="small" onClick={testRecaptcha} disabled={recaptchaTesting}>
                      {recaptchaTesting ? "Testing…" : "Test"}
                    </Button>
                  </Stack>
                  {recaptchaStatus && (
                    <Typography variant="caption" component="div" color="text.secondary">
                      {recaptchaStatus.error
                        ? `⚠️ ${recaptchaStatus.error}`
                        : !recaptchaStatus.secret_key_set
                          ? "reCAPTCHA is OFF — no keys configured yet."
                          : recaptchaStatus.secret_valid
                            ? `✅ reCAPTCHA active — secret key valid${recaptchaStatus.site_key_set ? "" : " (but the site key is missing, so the widget can't load it)"}.`
                            : "❌ Secret key rejected by Google — check the secret key."}
                    </Typography>
                  )}
                </Box>
              )}

              <Typography variant="subtitle1">Blocked IPs</Typography>
              <Stack direction="row" spacing={1}>
                <TextField label="IP address" value={newBlockedIp} onChange={(e) => setNewBlockedIp(e.target.value)} />
                <TextField label="Reason" value={newBlockedIpReason} onChange={(e) => setNewBlockedIpReason(e.target.value)} />
                <Button variant="outlined" onClick={addBlockedIp}>Add IP</Button>
              </Stack>
              <Stack spacing={1}>
                {blockedIps.map((item) => (
                  <Stack key={item.id} direction="row" spacing={1} alignItems="center">
                    <Chip label={item.ip_address} />
                    {item.reason && <Chip variant="outlined" label={item.reason} />}
                    <Button size="small" color="error" onClick={() => removeBlockedIp(item.id)}>Remove</Button>
                  </Stack>
                ))}
              </Stack>

              <Typography variant="subtitle1">Blocked Countries (GeoIP ISO alpha-2)</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <FormControl sx={{ minWidth: 280 }}>
                  <InputLabel id="blocked-country-select-label">Country</InputLabel>
                  <Select
                    labelId="blocked-country-select-label"
                    label="Country"
                    value={newBlockedCountry}
                    onChange={(e) => setNewBlockedCountry(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select…</em>
                    </MenuItem>
                    {countryReference.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="Reason" value={newBlockedCountryReason} onChange={(e) => setNewBlockedCountryReason(e.target.value)} sx={{ minWidth: 200 }} />
                <Button variant="outlined" onClick={addBlockedCountry} disabled={!effectiveTenantId}>
                  Add Country
                </Button>
              </Stack>
              <Stack spacing={1}>
                {blockedCountries.map((item) => (
                  <Stack key={item.id} direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={
                        countryLabelByCode[item.country_code]
                          ? `${countryLabelByCode[item.country_code]} (${item.country_code})`
                          : item.country_code
                      }
                    />
                    {item.reason && <Chip variant="outlined" label={item.reason} />}
                    <Button size="small" color="error" onClick={() => removeBlockedCountry(item.id)}>Remove</Button>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        </Stack>
      )}

      {section === "settings" && settingsSubsection === "branding" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Tenant Branding and Widget Settings</Typography>
            <Stack spacing={2}>
              <TextField label="Brand Name" value={brandName} onChange={(e) => setBrandName(e.target.value)} fullWidth />
              <TextField
                label="Widget Primary Color"
                value={widgetPrimaryColor}
                onChange={(e) => setWidgetPrimaryColor(e.target.value)}
                placeholder="#bf362e"
                fullWidth
              />
              <TextField
                label="Widget website URL (https://…)"
                value={widgetWebsiteUrl}
                onChange={(e) => setWidgetWebsiteUrl(e.target.value)}
                placeholder="https://example.com"
                fullWidth
              />
              <TextField
                label="Vector primary source_type (Qdrant payload)"
                value={widgetSourceType}
                onChange={(e) => setWidgetSourceType(e.target.value)}
                placeholder="Optional override (e.g. mrnwebdesigns_ie)"
                helperText="Leave empty to use Source DB type (woocommerce, magento, …) as Qdrant source_type. Set only when indexed payloads use a custom tag."
                fullWidth
              />
              <TextField
                label="User message bubble color"
                value={widgetUserMessageColor}
                onChange={(e) => setWidgetUserMessageColor(e.target.value)}
                placeholder="#bf362e"
                fullWidth
              />
              <TextField
                label="Bot message bubble color"
                value={widgetBotMessageColor}
                onChange={(e) => setWidgetBotMessageColor(e.target.value)}
                placeholder="#d5bbb9"
                fullWidth
              />
              <TextField
                label="User message text color"
                value={widgetUserMessageTextColor}
                onChange={(e) => setWidgetUserMessageTextColor(e.target.value)}
                placeholder="#ffffff"
                helperText="Hex color for text on the user bubble (contrast with bubble background)."
                fullWidth
              />
              <TextField
                label="Bot message text color"
                value={widgetBotMessageTextColor}
                onChange={(e) => setWidgetBotMessageTextColor(e.target.value)}
                placeholder="#1a1a1a"
                helperText="Hex color for text on the bot bubble (contrast with bubble background)."
                fullWidth
              />
              <TextField label="Widget Header Title" value={widgetHeaderTitle} onChange={(e) => setWidgetHeaderTitle(e.target.value)} fullWidth />
              <TextField
                label="Chat max results (general sites)"
                value={chatMaxResults}
                onChange={(e) => setChatMaxResults(e.target.value)}
                placeholder="empty = env default"
                helperText="Cap for Qdrant hits per message when the tenant is not WooCommerce-first (1–50, or leave empty)."
                fullWidth
              />
              <TextField
                label="Chat max results (WooCommerce catalog)"
                value={chatMaxResultsCatalog}
                onChange={(e) => setChatMaxResultsCatalog(e.target.value)}
                placeholder="empty = use general value or env catalog default"
                helperText="Optional override for WooCommerce tenants (1–50). Empty falls back to general max or server default."
                fullWidth
              />
              <TextField
                label="Welcome Message"
                value={widgetWelcomeMessage}
                onChange={(e) => setWidgetWelcomeMessage(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="Privacy Policy URL"
                value={privacyPolicyUrl}
                onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
                fullWidth
              />
              <Stack direction="row" spacing={2} alignItems="flex-start">
                {brandingAvatarPreviewSrc ? (
                  <Box component="img" src={brandingAvatarPreviewSrc} alt="Current widget avatar" sx={{ width: 96, height: 96, borderRadius: 1, objectFit: "cover", border: "1px solid", borderColor: "divider" }} />
                ) : (
                  <Box
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: 1,
                      border: "1px dashed",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "text.secondary",
                      typography: "caption",
                      textAlign: "center",
                      px: 1,
                    }}
                  >
                    {clearBrandingAvatar ? "Avatar removed after save" : "No avatar"}
                  </Box>
                )}
                <Stack spacing={1}>
                  <Button variant="outlined" component="label">
                    {avatarUpload ? `Selected: ${avatarUpload.name}` : "Upload avatar (max 10MB)"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        setAvatarUpload(e.target.files?.[0] || null);
                        if (e.target.files?.[0]) setClearBrandingAvatar(false);
                      }}
                    />
                  </Button>
                  {avatarUpload ? (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setAvatarUpload(null)}
                    >
                      Discard selected file
                    </Button>
                  ) : null}
                  {selectedTenant?.avatar_url ? (
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      disabled={!!avatarUpload}
                      onClick={() => setClearBrandingAvatar((v) => !v)}
                    >
                      {clearBrandingAvatar ? "Undo remove" : "Remove avatar"}
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
              <Button variant="contained" onClick={saveBrandingConfig} disabled={!sourceTenantId}>Save Branding</Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {section === "settings" && settingsSubsection === "moderation" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Automated replies</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Block words enforce moderation when a trigger matches. Quick replies are fuzzy-matched friendly canned answers (before search).
            </Typography>
            <Tabs value={moderationPanel} onChange={(_, v) => setModerationPanel(v)} sx={{ mb: 2 }}>
              <Tab label="Block word policies" />
              <Tab label="Quick replies (fuzzy)" />
            </Tabs>
            {moderationPanel === 0 && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Match Mode</InputLabel>
                  <Select label="Match Mode" value={newCategoryMode} onChange={(e) => setNewCategoryMode(e.target.value)}>
                    <MenuItem value="exact">Exact</MenuItem>
                    <MenuItem value="substring">Substring</MenuItem>
                    <MenuItem value="regex">Regex</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <TextField
                label="Category response message"
                value={newCategoryResponse}
                onChange={(e) => setNewCategoryResponse(e.target.value)}
                fullWidth
              />
              <Button variant="contained" onClick={createBlockWordCategory} disabled={!newCategoryName.trim() || !newCategoryResponse.trim()}>
                Add Category
              </Button>
              <Stack spacing={1.5}>
                {blockWordCategories.map((category) => (
                  <Card key={category.id} variant="outlined">
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <Chip label={category.name} />
                        <Chip variant="outlined" label={category.match_mode} />
                        <Button size="small" color="error" onClick={() => deleteBlockWordCategory(category.id)}>Delete Category</Button>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" mb={1}>{category.response_message}</Typography>
                      <Stack direction="row" spacing={1} mb={1}>
                        <TextField
                          size="small"
                          label="Add trigger word"
                          value={newWordByCategory[category.id] || ""}
                          onChange={(e) => setNewWordByCategory((prev) => ({ ...prev, [category.id]: e.target.value }))}
                        />
                        <Button size="small" variant="outlined" onClick={() => addWordToCategory(category.id)}>Add Word</Button>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(category.words || []).map((w) => (
                          <Chip
                            key={w.id}
                            label={w.word}
                            onDelete={() => deleteWordFromCategory(category.id, w.id)}
                            sx={{ mb: 1 }}
                          />
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
            )}
            {moderationPanel === 1 && (
            <Stack spacing={2}>
              <Alert severity="info">
                Response text supports placeholders: a dollar sign plus curly braces around brand_name, assistant_name (from widget header title), or website_url. Deleting a seeded row restores the built-in neutral default for that trigger. Add rows for pricing, contact, emergency wording, etc.
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TextField label="Category (e.g. greeting)" value={newQrCategory} onChange={(e) => setNewQrCategory(e.target.value)} sx={{ minWidth: 180 }} />
                <TextField label="Trigger phrase" value={newQrTrigger} onChange={(e) => setNewQrTrigger(e.target.value)} sx={{ minWidth: 200 }} placeholder="hello" />
                <TextField label="Priority" value={newQrPriority} onChange={(e) => setNewQrPriority(e.target.value)} sx={{ width: 100 }} />
                <TextField label="Fuzzy threshold (50–100, empty=default)" value={newQrThreshold} onChange={(e) => setNewQrThreshold(e.target.value)} sx={{ minWidth: 220 }} />
              </Stack>
              <TextField
                label="Response template"
                value={newQrTemplate}
                onChange={(e) => setNewQrTemplate(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <Button variant="contained" onClick={createQuickReply} disabled={!effectiveTenantId || !newQrTrigger.trim() || !newQrTemplate.trim()}>
                Add quick reply
              </Button>
              <Stack spacing={1}>
                {quickReplies.map((row) => (
                  <Card key={row.id} variant="outlined">
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={1}>
                        <Chip label={row.category} size="small" />
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="caption">Enabled</Typography>
                          <Switch checked={row.enabled} onChange={(e) => setQuickReplyEnabled(row, e.target.checked)} size="small" />
                        </Stack>
                        <Button size="small" color="error" onClick={() => deleteQuickReply(row.id)}>Delete</Button>
                      </Stack>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={1}>
                        <TextField
                          size="small"
                          label="Category"
                          value={(quickReplyDrafts[row.id]?.category ?? row.category) || ""}
                          onChange={(e) => updateQuickReplyDraft(row.id, { category: e.target.value })}
                          sx={{ minWidth: 160 }}
                        />
                        <TextField
                          size="small"
                          label="Trigger"
                          value={(quickReplyDrafts[row.id]?.trigger_phrase ?? row.trigger_phrase) || ""}
                          onChange={(e) => updateQuickReplyDraft(row.id, { trigger_phrase: e.target.value })}
                          sx={{ minWidth: 200 }}
                        />
                        <TextField
                          size="small"
                          label="Priority"
                          value={String(quickReplyDrafts[row.id]?.priority ?? row.priority ?? 0)}
                          onChange={(e) => updateQuickReplyDraft(row.id, { priority: e.target.value })}
                          sx={{ width: 100 }}
                        />
                        <TextField
                          size="small"
                          label="Threshold"
                          value={String(quickReplyDrafts[row.id]?.similarity_threshold ?? (row.similarity_threshold ?? ""))}
                          onChange={(e) => updateQuickReplyDraft(row.id, { similarity_threshold: e.target.value })}
                          sx={{ width: 120 }}
                        />
                      </Stack>
                      <TextField
                        size="small"
                        label="Response template"
                        value={(quickReplyDrafts[row.id]?.response_template ?? row.response_template) || ""}
                        onChange={(e) => updateQuickReplyDraft(row.id, { response_template: e.target.value })}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                      <Stack direction="row" spacing={1} mt={1}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => saveQuickReplyDraft(row)}
                          disabled={!isQuickReplyDirty(row)}
                        >
                          Save
                        </Button>
                        <Button size="small" variant="text" onClick={() => resetQuickReplyDraft(row)}>
                          Reset
                        </Button>
                      </Stack>
                      <Typography variant="caption" color="primary" sx={{ mt: 1, display: "block" }}>
                        Preview: {row.rendered_preview}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {section === "settings" && settingsSubsection === "db-settings" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Tenant Source Database Settings</Typography>
            <Typography color="text.secondary" mb={2}>
              Configure database connection details used to generate embeddings for each tenant.
            </Typography>
            <Stack spacing={2}>
              {canSelectTenant && (
                <FormControl size="small" sx={{ maxWidth: 420 }}>
                  <InputLabel>Tenant</InputLabel>
                  <Select
                    label="Tenant"
                    value={sourceTenantId}
                    onChange={(e) => persistSourceTenantId(e.target.value)}
                  >
                    {tenants.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                label="Source DB URL (DSN)"
                value={sourceDbUrl}
                onChange={(e) => setSourceDbUrl(e.target.value)}
                helperText="Example: mysql://user:password@host:3306/database"
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel id="source-db-type-label">Source DB Type</InputLabel>
                <Select
                  labelId="source-db-type-label"
                  label="Source DB Type"
                  value={sourceDbType}
                  onChange={(e) => applySourceDbTypeChange(e.target.value)}
                >
                  <MenuItem value="wordpress">WordPress</MenuItem>
                  <MenuItem value="woocommerce">WooCommerce</MenuItem>
                  <MenuItem value="magento">Magento</MenuItem>
                  <MenuItem value="static">Static-only</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="source-mode-label">Source Mode</InputLabel>
                <Select
                  labelId="source-mode-label"
                  label="Source Mode"
                  value={normalizeSourceModeForDbType(sourceDbType, sourceMode)}
                  onChange={(e) => setSourceMode(e.target.value)}
                  disabled={sourceDbType === "static"}
                >
                  {(SOURCE_MODE_BY_DB_TYPE[sourceDbType] || SOURCE_MODE_BY_DB_TYPE.wordpress).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={sourceDbType === "magento" ? "Table prefix (optional)" : "Table Prefix"}
                value={sourceTablePrefix}
                onChange={(e) => setSourceTablePrefix(e.target.value)}
                helperText={sourceDbType === "magento" ? "Leave empty for standard Magento 2 table names" : undefined}
                fullWidth
              />
              <TextField
                label={sourceDbType === "magento" ? "Magento store ID" : "URL Table"}
                value={sourceUrlTable}
                onChange={(e) => setSourceUrlTable(e.target.value)}
                helperText={sourceDbType === "magento" ? "Default store view ID (usually 1)" : undefined}
                fullWidth
              />
              <TextField
                label="Canonical Base URL"
                value={sourceCanonicalBaseUrl}
                onChange={(e) => setSourceCanonicalBaseUrl(e.target.value)}
                helperText="Used to normalize equivalent hosts (e.g. prod/staging)"
                fullWidth
              />
              <TextField
                label="Domain Aliases"
                value={sourceDomainAliases}
                onChange={(e) => setSourceDomainAliases(e.target.value)}
                helperText="Comma or newline separated URLs to treat as aliases"
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="Static Source URLs (JSON array or newline list)"
                value={sourceStaticUrlsJson}
                onChange={(e) => setSourceStaticUrlsJson(e.target.value)}
                helperText='Examples: ["https://www.chilliapple.co.uk/"] or one URL per line'
                fullWidth
                multiline
                minRows={6}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" onClick={saveSourceConfig} disabled={!sourceTenantId}>Save Source Settings</Button>
                {selectedTenant && <Chip label={selectedTenant.name} />}
                {role === "superadmin" && selectedTenant && (
                  <Chip variant="outlined" label={`Tenant ID: ${selectedTenant.id}`} />
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {section === "settings" && settingsSubsection === "retrieval-profile" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Retrieval profile</Typography>
            <Typography color="text.secondary" mb={2}>
              Built automatically after each catalog reindex (facets, variants, category gazetteer).
              Superadmins can edit gazetteer match flags or replace the full JSON.
            </Typography>
            <Stack spacing={2}>
              {canSelectTenant && (
                <FormControl size="small" sx={{ maxWidth: 420 }}>
                  <InputLabel>Tenant</InputLabel>
                  <Select
                    label="Tenant"
                    value={sourceTenantId}
                    onChange={(e) => persistSourceTenantId(e.target.value)}
                  >
                    {tenants.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="outlined" onClick={loadRetrievalProfile} disabled={!effectiveTenantId || retrievalProfileLoading}>
                  Refresh
                </Button>
                {role === "superadmin" && retrievalProfile?.profile && (
                  <>
                    <Button variant="contained" onClick={openProfileJsonEditor} disabled={profileSaving}>
                      Edit JSON
                    </Button>
                    {Object.keys(gazetteerDrafts).length > 0 && (
                      <Button variant="contained" color="secondary" onClick={saveGazetteerFlags} disabled={profileSaving}>
                        Save gazetteer flags
                      </Button>
                    )}
                  </>
                )}
                {selectedTenant && <Chip label={selectedTenant.name} />}
              </Stack>
              {retrievalProfileLoading && <LinearProgress />}
              {!retrievalProfileLoading && retrievalProfile && !retrievalProfile.profile && (
                <Alert severity="info">
                  No profile — run a catalog reindex for this tenant.
                </Alert>
              )}
              {!retrievalProfileLoading && retrievalProfile?.profile && (() => {
                const profile = retrievalProfile.profile;
                const stats = profile.stats || {};
                const facets = Object.entries(profile.facets || {}).sort(([a], [b]) => a.localeCompare(b));
                const gazetteer = profile.category_strategy?.gazetteer || [];
                const coreFields = profile.core_fields || {};
                const variantAvailability = profile.variant_availability || {};
                return (
                  <>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`Version ${retrievalProfile.retrieval_profile_version ?? profile.profile_version ?? "—"}`} />
                      <Chip label={`${stats.product_count ?? 0} products`} />
                      <Chip label={`${stats.facet_count ?? facets.length} facets`} />
                      {stats.configurable_with_variants != null && (
                        <Chip label={`${stats.configurable_with_variants} with variants`} variant="outlined" />
                      )}
                      {profile.generated_at && <Chip label={`Generated ${profile.generated_at}`} variant="outlined" />}
                    </Stack>
                    {variantAvailability.matrix_qdrant_key && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Variant availability uses Qdrant key &quot;{variantAvailability.matrix_qdrant_key}&quot;
                        {variantAvailability.display_facet_qdrant_path
                          ? ` with display path ${variantAvailability.display_facet_qdrant_path}`
                          : ""}.
                      </Alert>
                    )}
                    {Object.keys(coreFields).length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>Core fields</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {Object.entries(coreFields).map(([key, meta]) => (
                            <Chip
                              key={key}
                              label={`${key}: ${meta.coverage_pct ?? 0}%${meta.indexed ? "" : " (not indexed)"}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Facets</Typography>
                      {facets.length === 0 ? (
                        <Typography color="text.secondary">No facets above coverage threshold.</Typography>
                      ) : (
                        <Stack spacing={2}>
                          {facets.map(([facetId, facet]) => (
                            <Box key={facetId} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography fontWeight={600}>{facetId}</Typography>
                                <Chip size="small" label={`${facet.coverage_pct ?? 0}% coverage`} />
                                <Chip size="small" label={`${facet.product_count ?? 0} products`} variant="outlined" />
                                {facet.indexed && <Chip size="small" label="indexed" color="success" variant="outlined" />}
                              </Stack>
                              {(facet.sample_values || []).length > 0 && (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                  {(facet.sample_values || []).map((v) => (
                                    <Chip key={`${facetId}-${v}`} size="small" label={v} />
                                  ))}
                                </Stack>
                              )}
                              {facet.value_aliases && Object.keys(facet.value_aliases).length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Button
                                    size="small"
                                    onClick={() => setExpandedFacetAliases((prev) => ({ ...prev, [facetId]: !prev[facetId] }))}
                                  >
                                    {expandedFacetAliases[facetId] ? "Hide" : "Show"} value aliases ({Object.keys(facet.value_aliases).length})
                                  </Button>
                                  {expandedFacetAliases[facetId] && (
                                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                      {Object.entries(facet.value_aliases).map(([alias, canonical]) => (
                                        <Typography key={`${facetId}-${alias}`} variant="body2" color="text.secondary">
                                          {alias} → {canonical}
                                        </Typography>
                                      ))}
                                    </Stack>
                                  )}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Category gazetteer</Typography>
                      {gazetteer.length === 0 ? (
                        <Typography color="text.secondary">No category entries.</Typography>
                      ) : (
                        <Stack spacing={1}>
                          {gazetteer.map((entry) => {
                            const draft = gazetteerDrafts[entry.id] || {};
                            const hardFilter = draft.hard_filter ?? Boolean(entry.hard_filter);
                            return (
                            <Box key={entry.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography fontWeight={600}>{entry.id}</Typography>
                                {role === "superadmin" && (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={hardFilter}
                                        onChange={(e) => setGazetteerDrafts((prev) => ({
                                          ...prev,
                                          [entry.id]: { ...prev[entry.id], hard_filter: e.target.checked },
                                        }))}
                                      />
                                    }
                                    label="Hard Qdrant filter"
                                  />
                                )}
                                {role !== "superadmin" && entry.hard_filter && (
                                  <Chip size="small" label="hard filter" color="warning" variant="outlined" />
                                )}
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                Labels: {(entry.labels || []).join(", ") || "—"}
                              </Typography>
                              {(entry.aliases && Object.keys(entry.aliases).length > 0) && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Aliases: {Object.entries(entry.aliases).map(([k, v]) => `${k}→${v}`).join(", ")}
                                </Typography>
                              )}
                            </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </>
                );
              })()}
            </Stack>
          </CardContent>
        </Card>
      )}
      </Box>
      </Box>

      <Dialog open={profileJsonOpen} onClose={() => setProfileJsonOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit retrieval profile (superadmin)</DialogTitle>
        <DialogContent>
          <TextField
            value={profileJsonDraft}
            onChange={(e) => setProfileJsonDraft(e.target.value)}
            multiline
            minRows={16}
            fullWidth
            sx={{ mt: 1, fontFamily: "monospace" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileJsonOpen(false)}>Cancel</Button>
          <Button onClick={saveProfileJson} variant="contained" disabled={profileSaving}>
            Save profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{role === "superadmin" ? "Create Admin/Manager" : "Create Manager"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} fullWidth />
            <TextField label="Password" type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)} disabled={role === "admin"}>
                {role === "superadmin" && <MenuItem value="admin">Admin</MenuItem>}
                <MenuItem value="manager">Manager</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Tenant Option</InputLabel>
              <Select label="Tenant Option" value={adminTenantMode} onChange={(e) => setAdminTenantMode(e.target.value)}>
                <MenuItem value="existing">Use Existing Tenant</MenuItem>
                <MenuItem value="new">Create New Tenant</MenuItem>
              </Select>
            </FormControl>
            {adminTenantMode === "new" ? (
              <TextField
                label="New Tenant Name"
                value={newTenantName}
                onChange={(e) => setNewTenantName(e.target.value)}
                fullWidth
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Tenant</InputLabel>
                <Select label="Tenant" value={adminTenantId} onChange={(e) => setAdminTenantId(e.target.value)}>
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                      {role === "superadmin" ? ` (${t.id})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={createAdmin}
            variant="contained"
            disabled={
              !adminEmail ||
              !adminPassword ||
              !newUserRole ||
              (adminTenantMode === "new" ? !newTenantName.trim() : !adminTenantId)
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manageTenantsOpen} onClose={() => setManageTenantsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage User Tenants</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {manageTenantUser ? `User: ${manageTenantUser.email} (${manageTenantUser.role})` : ""}
            </Typography>
            <FormControl fullWidth>
              {manageTenantUser?.role === "manager" ? (
                <RadioGroup
                  value={manageTenantIds[0] || ""}
                  onChange={(e) => setManageTenantIds(e.target.value ? [e.target.value] : [])}
                >
                  {tenants.map((t) => (
                    <FormControlLabel
                      key={t.id}
                      value={t.id}
                      control={<Radio size="small" />}
                      label={`${t.name}${role === "superadmin" ? ` (${t.id})` : ""}`}
                    />
                  ))}
                </RadioGroup>
              ) : (
                <FormGroup>
                  {tenants.map((t) => (
                    <FormControlLabel
                      key={t.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={manageTenantIds.includes(t.id)}
                          onChange={(e) => {
                            setManageTenantIds((prev) =>
                              e.target.checked ? [...new Set([...prev, t.id])] : prev.filter((id) => id !== t.id)
                            );
                          }}
                        />
                      }
                      label={`${t.name}${role === "superadmin" ? ` (${t.id})` : ""}`}
                    />
                  ))}
                </FormGroup>
              )}
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Managers can only have one tenant. Admins can have multiple.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageTenantsOpen(false)} disabled={manageTenantSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveManageUserTenants}
            disabled={
              !manageTenantUser ||
              manageTenantSaving ||
              (manageTenantUser?.role === "manager" ? manageTenantIds.length !== 1 : false)
            }
          >
            Save Associations
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createTenantOpen} onClose={() => setCreateTenantOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Tenant</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            label="Tenant name"
            value={newStandaloneTenantName}
            onChange={(e) => setNewStandaloneTenantName(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTenantOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createStandaloneTenant} disabled={!newStandaloneTenantName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset User Password</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            type="password"
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={resetPassword} disabled={!newPassword}>Reset</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
